/**
 * App do Cliente — PWA mobile-first
 * Login: CPF + Placa + Senha → JWT armazenado em localStorage
 * Páginas: Login, Trocar Senha, Home, Pagar, Histórico, Perfil
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Bell, Home, Receipt, CreditCard, User, LogOut, Copy, Check, Upload, ArrowLeft, X, Eye, Bike, Wrench, AlertTriangle, CheckCircle2, Gauge, Smartphone, Share, Plus } from 'lucide-react';
import ClienteAtendimentoScreen from './ClienteAtendimentoScreen';
import AtendimentoFloatingButton from './AtendimentoFloatingButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'cliente_app_token';

const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const tk = localStorage.getItem(TOKEN_KEY);
  if (tk) cfg.headers.Authorization = `Bearer ${tk}`;
  return cfg;
});

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

/**
 * Abre o anexo (PDF ou imagem) de uma multa de trânsito numa nova aba.
 * Faz fetch autenticado, gera blob URL e abre. Funciona em iOS PWA.
 */
const abrirArquivoMulta = async (cobrancaId) => {
  try {
    const resp = await api.get(`/cliente/cobrancas-extras/${cobrancaId}/arquivo`, {
      responseType: 'blob',
    });
    const tipoMime = resp.headers?.['content-type'] || 'application/pdf';
    const blob = new Blob([resp.data], { type: tipoMime });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank', 'noopener,noreferrer');
    // Se o navegador bloqueou popup (iOS strict), faz download
    if (!w) {
      const a = document.createElement('a');
      a.href = url;
      a.download = `multa-${cobrancaId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // Libera memória depois de um tempo
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    alert('Não foi possível abrir o arquivo da multa.');
    console.error('abrirArquivoMulta error:', e);
  }
};
const fmtData = (d) => {
  if (!d) return '—';
  try {
    // Strings ISO "YYYY-MM-DD" devem ser interpretadas como hora LOCAL,
    // não UTC. Sem o "T00:00:00", JS interpreta como UTC midnight e o
    // fuso de Brasília (UTC-3) joga o dia 1 pra trás. Adicionamos a hora
    // local explicitamente para alinhar com o painel admin.
    let s = String(d);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      s = s + 'T00:00:00';
    }
    return new Date(s).toLocaleDateString('pt-BR');
  } catch { return d; }
};

// ============================================================
// WEB PUSH — pedir permissão + registrar subscription
// ============================================================
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

async function ativarPushNotifications({ forcarRecriar = false } = {}) {
  try {
    if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
      return { ok: false, motivo: 'Navegador não suporta push' };
    }
    if (Notification.permission === 'denied') {
      return { ok: false, motivo: 'Permissão negada — habilite nas configurações do navegador' };
    }
    if (Notification.permission === 'default') {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') return { ok: false, motivo: 'Permissão recusada' };
    }
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    // Se forçar recriar (revalidação manual pelo cliente), descarta o subscription
    // existente — útil quando o SW foi atualizado e o sub antigo está vinculado
    // ao SW velho que não tem push handler.
    if (sub && forcarRecriar) {
      try { await sub.unsubscribe(); } catch (e) { /* ignora */ }
      try { await api.post('/cliente/push/unsubscribe', { endpoint: sub.endpoint }); } catch (e) { /* ignora */ }
      sub = null;
    }
    if (!sub) {
      const { data: { public_key } } = await api.get('/cliente/push/public-key');
      if (!public_key) return { ok: false, motivo: 'VAPID não configurado no servidor' };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
    }
    await api.post('/cliente/push/subscribe', sub.toJSON());
    return { ok: true };
  } catch (e) {
    console.warn('Erro ao ativar push:', e);
    return { ok: false, motivo: e.message };
  }
}

// ============================================================
// LOGIN
// ============================================================
const LoginScreen = ({ onLogin }) => {
  const [cpf, setCpf] = useState('');
  const [placa, setPlaca] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      const { data } = await api.post('/cliente/auth/login', { cpf, placa, senha });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onLogin(data);
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao entrar');
    } finally {
      setCarregando(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden" style={{ backgroundColor: '#060A14', minHeight: '100dvh' }} translate="no">
      {/* HERO IMAGE — logo neon Desloca + moto DK 160 */}
      <div
        className="relative w-full"
        style={{
          height: 'min(46vh, 380px)',
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_dcfaebc2-4990-416f-8985-7fe16afdbc64/artifacts/q9xp59gx_5311ad87-d6e6-42f7-a11b-8f033aa783e8.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
        data-testid="cliente-login-hero"
      >
        {/* Fade gradiente do hero pro fundo escuro (transição suave) */}
        <div
          className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
          style={{
            background: 'linear-gradient(to bottom, rgba(6,10,20,0) 0%, rgba(6,10,20,0.85) 70%, #060A14 100%)',
          }}
        />
      </div>

      {/* Glow neon ambiental ao redor do form */}
      <div
        className="pointer-events-none absolute"
        style={{
          top: '38vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '420px',
          height: '300px',
          background: 'radial-gradient(ellipse at center, rgba(0, 255, 102, 0.18) 0%, rgba(0, 255, 102, 0) 70%)',
          filter: 'blur(40px)',
        }}
      />

      {/* FORM */}
      <div className="flex-1 flex items-start justify-center px-5 pb-8 relative z-10" style={{ marginTop: '-32px' }}>
        <div className="w-full max-w-sm">
          <div className="text-center mb-5">
            <p className="text-xs uppercase tracking-[0.2em]" style={{ color: '#00FF66', textShadow: '0 0 12px rgba(0, 255, 102, 0.6)' }}>
              Locadora de Motos
            </p>
            <h2 className="text-lg font-semibold mt-2" style={{ color: '#E2E8F0', fontFamily: 'Outfit, sans-serif' }}>
              Acesso do Cliente
            </h2>
          </div>
          <form
            onSubmit={submit}
            className="border-0 rounded-3xl p-6 space-y-4"
            style={{
              background: 'linear-gradient(145deg, rgba(16, 22, 38, 0.92) 0%, rgba(11, 16, 29, 0.88) 100%)',
              border: '1px solid rgba(0, 255, 102, 0.18)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: '0 24px 48px rgba(0,0,0,0.5), 0 0 32px rgba(0, 255, 102, 0.08)',
            }}
          >
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#94A3B8' }}>CPF</label>
              <input
                type="text" inputMode="numeric" value={cpf} onChange={(e) => setCpf(e.target.value)}
                placeholder="000.000.000-00" autoComplete="off"
                className="w-full rounded-xl px-4 py-3 mt-1.5 text-white text-base focus:outline-none transition-colors"
                style={{
                  background: 'rgba(6, 10, 20, 0.7)',
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                }}
                onFocus={(e) => e.target.style.borderColor = '#00FF66'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(148, 163, 184, 0.18)'}
                data-testid="cliente-input-cpf"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#94A3B8' }}>Placa da Moto</label>
              <input
                type="text" value={placa} onChange={(e) => setPlaca(e.target.value.toUpperCase())}
                placeholder="ABC1D23" autoComplete="off"
                className="w-full rounded-xl px-4 py-3 mt-1.5 text-white text-base uppercase tracking-widest focus:outline-none transition-colors"
                style={{
                  background: 'rgba(6, 10, 20, 0.7)',
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                }}
                onFocus={(e) => e.target.style.borderColor = '#00FF66'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(148, 163, 184, 0.18)'}
                data-testid="cliente-input-placa"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: '#94A3B8' }}>Senha</label>
              <input
                type="password" value={senha} onChange={(e) => setSenha(e.target.value)}
                placeholder="••••••" autoComplete="current-password"
                className="w-full rounded-xl px-4 py-3 mt-1.5 text-white text-base focus:outline-none transition-colors"
                style={{
                  background: 'rgba(6, 10, 20, 0.7)',
                  border: '1px solid rgba(148, 163, 184, 0.18)',
                }}
                onFocus={(e) => e.target.style.borderColor = '#00FF66'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(148, 163, 184, 0.18)'}
                data-testid="cliente-input-senha"
              />
            </div>
            {erro && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-xl p-3" data-testid="cliente-erro-login">
                {erro}
              </div>
            )}
            <button
              type="submit" disabled={carregando}
              className="w-full cliente-btn-pill py-3.5 transition mt-1"
              data-testid="cliente-btn-entrar"
            >
              {carregando ? 'Entrando...' : 'Entrar'}
            </button>
            <p className="text-xs text-center pt-1" style={{ color: '#64748B' }}>
              Não tem acesso? Solicite à locadora.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// TROCAR SENHA
// ============================================================
const TrocarSenhaScreen = ({ onSucesso, onLogout }) => {
  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (nova !== confirmar) { setErro('Senha nova e confirmação não conferem'); return; }
    if (nova.length < 6) { setErro('Senha precisa ter no mínimo 6 caracteres'); return; }
    setErro(''); setCarregando(true);
    try {
      const { data } = await api.post('/cliente/auth/trocar-senha', { senha_atual: atual, senha_nova: nova });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      onSucesso();
    } catch (e) {
      setErro(e.response?.data?.detail || 'Erro ao trocar senha');
    } finally { setCarregando(false); }
  };

  return (
    <div className="min-h-screen bg-[#060A14] flex items-center justify-center px-4" translate="no">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold text-[#00FF66] mb-1">Criar nova senha</h1>
        <p className="text-sm text-[#94A3B8] mb-6">No primeiro acesso, você precisa criar uma senha pessoal.</p>
        <form onSubmit={submit} className="cliente-card border-0 rounded-2xl p-6 space-y-4">
          <input type="password" value={atual} onChange={(e) => setAtual(e.target.value)} placeholder="Senha atual (a temporária)"
            className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-3 focus:outline-none focus:border-emerald-500"
            data-testid="cliente-input-senha-atual" />
          <input type="password" value={nova} onChange={(e) => setNova(e.target.value)} placeholder="Nova senha (min. 6 caracteres)"
            className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-3 focus:outline-none focus:border-emerald-500"
            data-testid="cliente-input-senha-nova" />
          <input type="password" value={confirmar} onChange={(e) => setConfirmar(e.target.value)} placeholder="Confirme a nova senha"
            className="w-full bg-slate-800 border border-slate-700 text-white rounded-lg px-3 py-3 focus:outline-none focus:border-emerald-500"
            data-testid="cliente-input-senha-confirmar" />
          {erro && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3">{erro}</div>}
          <button type="submit" disabled={carregando}
            className="w-full cliente-btn-pill py-3"
            data-testid="cliente-btn-salvar-senha">
            {carregando ? 'Salvando...' : 'Criar senha e continuar'}
          </button>
          <button type="button" onClick={onLogout} className="w-full text-[#94A3B8] text-sm hover:text-white">Sair</button>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// CARD DE PAGAMENTO PENDENTE (compacto) + Modal de detalhes
// ============================================================
const PagamentoCard = ({ pag, onPagar, pixIguais = false, temAluguelPendente = false, temDebitoPendente = false }) => {
  const [verDetalhes, setVerDetalhes] = useState(false);
  const [detalhes, setDetalhes] = useState(null);
  const [carregandoDet, setCarregandoDet] = useState(false);

  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const venc = new Date((pag.data_vencimento || '') + 'T00:00:00');
  const delta = Math.round((venc - hoje) / 86400000);
  const atrasado = delta < 0;
  const diasAtraso = atrasado ? Math.abs(delta) : 0;
  const bloqueada = diasAtraso >= 3;
  const ultimoDiaTolerancia = diasAtraso === 2; // ⚠️ último aviso antes do bloqueio

  const cor = bloqueada ? '#FF2A5F' : (ultimoDiaTolerancia ? '#FF6B35' : (atrasado ? '#FFB800' : '#00FF66'));
  const corBg = bloqueada ? 'rgba(255, 42, 95, 0.10)' : (ultimoDiaTolerancia ? 'rgba(255, 107, 53, 0.12)' : (atrasado ? 'rgba(255, 184, 0, 0.10)' : 'rgba(0, 255, 102, 0.10)'));
  const corBorda = bloqueada ? 'rgba(255, 42, 95, 0.40)' : (ultimoDiaTolerancia ? 'rgba(255, 107, 53, 0.55)' : (atrasado ? 'rgba(255, 184, 0, 0.40)' : 'rgba(0, 255, 102, 0.30)'));
  const corGlow = bloqueada ? 'rgba(255, 42, 95, 0.15)' : (ultimoDiaTolerancia ? 'rgba(255, 107, 53, 0.18)' : (atrasado ? 'rgba(255, 184, 0, 0.10)' : 'rgba(0, 255, 102, 0.06)'));

  let statusLabel;
  if (bloqueada) statusLabel = `🔒 ${diasAtraso}d atraso`;
  else if (ultimoDiaTolerancia) statusLabel = `🚨 ÚLTIMO DIA`;
  else if (atrasado) statusLabel = `⚠️ ${diasAtraso}d atraso`;
  else if (delta === 0) statusLabel = '⏰ Vence hoje';
  else if (delta <= 2) statusLabel = `✓ ${delta}d`;
  else statusLabel = `✓ ${delta}d`;

  const isDebito = pag._tipo === 'debito';
  const parcelaLabel = pag._parcela_label;
  const labelTipo = isDebito
    ? `${pag._descricao_debito || 'Débito'}${parcelaLabel ? ` ${parcelaLabel}` : ''}`
    : 'Semanal';
  const iconeTipo = isDebito ? '⚠️' : '🏍️';

  const carregarDetalhes = async () => {
    if (detalhes || isDebito) { setVerDetalhes(true); return; }
    setCarregandoDet(true);
    setVerDetalhes(true);
    try {
      const { data } = await api.get(`/cliente/pagamentos/${pag.id}/detalhes`);
      setDetalhes(data);
    } catch (e) {
      // Fallback silencioso: o modal ainda mostra info básica
    } finally {
      setCarregandoDet(false);
    }
  };

  return (
    <>
      <div
        className="rounded-2xl p-4 relative"
        style={{
          background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
          border: `1px solid ${corBorda}`,
          boxShadow: `0 4px 16px rgba(0,0,0,0.3), 0 0 12px ${corGlow}`,
        }}
        data-testid={`cliente-card-pagamento-${pag.id}`}
        data-tipo={pag._tipo || 'semanal'}
      >
        {/* Topo: tipo + status */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <div
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{
              backgroundColor: isDebito ? 'rgba(255, 184, 0, 0.12)' : 'rgba(0, 195, 255, 0.12)',
              color: isDebito ? '#FFB800' : '#00C3FF',
              border: `1px solid ${isDebito ? 'rgba(255, 184, 0, 0.4)' : 'rgba(0, 195, 255, 0.4)'}`,
            }}
            data-testid={`cliente-tipo-pendencia-${pag.id}`}
          >
            <span>{iconeTipo}</span>
            <span className="truncate max-w-[120px]">{labelTipo}</span>
          </div>
          <div
            className="px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0"
            style={{ backgroundColor: corBg, color: cor, border: `1px solid ${corBorda}` }}
            data-testid="cliente-status-pagamento"
          >
            {statusLabel}
          </div>
        </div>

        {/* Valor + vencimento (compacto)
            🆕 Mostra apenas o ALUGUEL (+ multa/juros se atrasado), SEM débitos —
            débitos vão em PIX separado e aparecem como pendência separada. */}
        {(() => {
          const valAluguel = Number(pag.valor_aluguel ?? pag.valor ?? 0);
          const valTotalFull = Number(pag.valor_atualizado != null ? pag.valor_atualizado : pag.valor || 0);
          const valBaseFull = Number(pag.valor || 0);
          const acrescAtraso = Math.max(0, valTotalFull - valBaseFull);
          const valorMostrado = isDebito ? Number(pag.valor || 0) : (valAluguel + acrescAtraso);
          return (
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-2xl font-bold tracking-tight" style={{ color: cor, fontFamily: 'Outfit, sans-serif' }} data-testid={`cliente-valor-atualizado-${pag.id}`}>
                {fmt(valorMostrado)}
              </div>
              <div className="text-xs text-right" style={{ color: '#94A3B8' }}>
                <div>vence {fmtData(pag.data_vencimento)}</div>
                {!isDebito && acrescAtraso > 0 && (
                  <div className="text-[10px] mt-0.5" style={{ color: '#FFB800' }}>
                    + R$ {acrescAtraso.toFixed(2).replace('.', ',')} multa+juros
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* 🆕 Detalhamento de débitos consolidados — quando há +1 débito no mesmo dia */}
        {pag._consolidado && Array.isArray(pag._itens) && pag._itens.length > 1 && (
          <div
            className="mt-3 rounded-xl border px-3 py-2"
            style={{
              background: 'rgba(255, 184, 0, 0.06)',
              borderColor: 'rgba(255, 184, 0, 0.25)',
            }}
            data-testid={`cliente-debitos-detalhamento-${pag.id}`}
          >
            <div className="text-[10px] uppercase tracking-widest font-semibold mb-1.5" style={{ color: '#FFB800' }}>
              Detalhamento ({pag._itens.length} cobranças)
            </div>
            <ul className="space-y-1">
              {pag._itens.map((it, i) => (
                <li
                  key={it.id || i}
                  className="flex items-center justify-between gap-3 text-xs"
                  style={{ color: '#E2E8F0' }}
                >
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span style={{ color: '#FFB800' }}>•</span>
                    <span className="truncate">{it.descricao}</span>
                    {it.parcela && (
                      <span
                        className="text-[10px] font-semibold rounded px-1 py-px"
                        style={{ background: 'rgba(255, 184, 0, 0.15)', color: '#FFB800' }}
                      >
                        {it.parcela}
                      </span>
                    )}
                    {it.tem_arquivo && it.tipo === 'multa_transito' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); abrirArquivoMulta(it.cobranca_id); }}
                        className="text-[10px] underline ml-1"
                        style={{ color: '#FFB800' }}
                        title="Ver PDF da multa"
                        data-testid={`cliente-link-pdf-${it.cobranca_id}`}
                      >
                        📄 PDF
                      </button>
                    )}
                  </div>
                  <span className="font-semibold tabular-nums whitespace-nowrap" style={{ color: '#FFB800' }}>
                    {fmt(it.valor)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 🚨 Aviso "último dia de tolerância" — só aparece no 2º dia de atraso */}
        {ultimoDiaTolerancia && (
          <div
            className="mt-3 rounded-xl px-3 py-2 flex items-start gap-2"
            style={{
              background: 'linear-gradient(90deg, rgba(255, 107, 53, 0.18) 0%, rgba(255, 42, 95, 0.12) 100%)',
              border: '1px solid rgba(255, 107, 53, 0.55)',
            }}
            data-testid={`cliente-aviso-ultimo-dia-${pag.id}`}
          >
            <span className="text-base leading-none mt-0.5">🚨</span>
            <div className="text-[11px] leading-tight" style={{ color: '#FFD7C2', fontFamily: 'DM Sans, sans-serif' }}>
              <div className="font-bold uppercase tracking-wider" style={{ color: '#FF6B35' }}>
                Último dia de tolerância
              </div>
              <div className="mt-0.5">
                Pague até <b>23:59h de hoje</b>. Após <b>00h</b> sua moto será <b>bloqueada automaticamente</b>.
              </div>
            </div>
          </div>
        )}

        {/* 💡 Hint "PIX igual — pode pagar junto" — quando o PIX do aluguel == PIX dos débitos
            e há pendência do outro tipo no carrossel. */}
        {pixIguais && ((isDebito && temAluguelPendente) || (!isDebito && temDebitoPendente)) && (
          <div
            className="mt-3 rounded-xl px-3 py-2 flex items-center gap-2"
            style={{
              background: 'rgba(0, 195, 255, 0.10)',
              border: '1px solid rgba(0, 195, 255, 0.40)',
            }}
            data-testid={`cliente-hint-pix-igual-${pag.id}`}
          >
            <span className="text-base leading-none">💡</span>
            <div className="text-[11px] leading-tight" style={{ color: '#A5E5FF', fontFamily: 'DM Sans, sans-serif' }}>
              <b style={{ color: '#00C3FF' }}>Mesmo PIX do {isDebito ? 'aluguel' : 'débito'}</b> — você pode pagar tudo em uma única transferência.
            </div>
          </div>
        )}

        {/* 🆕 Botão "Ver Multa (PDF)" — quando é multa de trânsito com anexo */}
        {isDebito && !pag._consolidado && (pag.arquivo_path_r2 || pag.arquivo_path) && (pag.tipo === 'multa_transito') && (
          <button
            onClick={() => abrirArquivoMulta(pag.id)}
            className="mt-3 w-full py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all"
            style={{
              background: 'rgba(255, 184, 0, 0.10)',
              border: '1px solid rgba(255, 184, 0, 0.40)',
              color: '#FFB800',
              fontFamily: 'DM Sans, sans-serif',
            }}
            data-testid={`cliente-btn-ver-multa-pdf-${pag.id}`}
          >
            📄 Ver auto de infração (PDF/imagem)
          </button>
        )}

        {/* Botões compactos */}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button
            onClick={carregarDetalhes}
            className="py-2 px-3 rounded-full text-xs font-semibold transition-all"
            style={{
              backgroundColor: 'transparent',
              color: '#E2E8F0',
              border: '1px solid rgba(148, 163, 184, 0.3)',
              fontFamily: 'DM Sans, sans-serif',
            }}
            data-testid={`cliente-btn-detalhes-${pag.id}`}
          >
            Ver detalhes
          </button>
          <button
            onClick={() => onPagar(pag)}
            className="py-2 px-3 rounded-full text-xs font-bold transition-all"
            style={{
              backgroundColor: cor,
              color: '#060A14',
              boxShadow: `0 0 12px ${corGlow}`,
              fontFamily: 'DM Sans, sans-serif',
            }}
            data-testid="cliente-btn-pagar-agora"
          >
            {bloqueada ? 'PAGAR · LIBERAR' : 'PAGAR AGORA'}
          </button>
        </div>
      </div>

      {/* MODAL DE DETALHES — renderizado via Portal em document.body para escapar
          do stacking context do carrossel (WebkitOverflowScrolling: touch no iOS Safari
          captura position:fixed dos descendentes). */}
      {verDetalhes && createPortal(
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setVerDetalhes(false)}
          data-testid={`cliente-modal-detalhes-${pag.id}`}
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px))' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[85vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(180deg, #0F1626 0%, #060A14 100%)',
              border: `1px solid ${corBorda}`,
              boxShadow: `0 -8px 32px rgba(0,0,0,0.6), 0 0 30px ${corGlow}`,
              marginBottom: '0px',
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-wider" style={{ color: '#94A3B8' }}>Detalhes da pendência</div>
                <div className="text-base font-bold text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {iconeTipo} {labelTipo}
                </div>
              </div>
              <button
                onClick={() => setVerDetalhes(false)}
                className="text-[#94A3B8] hover:text-white p-1"
                aria-label="Fechar"
                data-testid={`cliente-btn-fechar-detalhes-${pag.id}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between items-baseline">
                <span className="text-sm" style={{ color: '#94A3B8' }}>Vencimento</span>
                <span className="text-sm text-white">{fmtData(pag.data_vencimento)}</span>
              </div>

              {!isDebito && carregandoDet && (
                <div className="text-center text-sm py-4" style={{ color: '#94A3B8' }}>Calculando juros e multa...</div>
              )}

              {!isDebito && detalhes && (
                <>
                  <div className="border-t border-white/5 my-2"></div>
                  <div className="flex justify-between items-baseline">
                    <span className="text-sm" style={{ color: '#94A3B8' }}>Aluguel</span>
                    <span className="text-sm text-white">{fmt(detalhes.valor_original)}</span>
                  </div>
                  {detalhes.em_atraso && detalhes.dias_atraso > 0 && (
                    <>
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm" style={{ color: '#FFB800' }}>Multa por atraso (R$ 15,00 fixos)</span>
                        <span className="text-sm" style={{ color: '#FFB800' }}>+ {fmt(detalhes.multa)}</span>
                      </div>
                      <div className="flex justify-between items-baseline">
                        <span className="text-sm" style={{ color: '#FFB800' }}>
                          Juros ({detalhes.dias_atraso} dia{detalhes.dias_atraso > 1 ? 's' : ''} × R$ 7,00)
                        </span>
                        <span className="text-sm" style={{ color: '#FFB800' }}>+ {fmt(detalhes.juros)}</span>
                      </div>
                    </>
                  )}
                  <div className="border-t border-white/10 my-2"></div>
                  {(() => {
                    // Total da semana SOMENTE aluguel + multa + juros (sem débitos —
                    // débitos aparecem como pendência separada paga em PIX diferente).
                    const totalSemana = Number(detalhes.valor_original || 0)
                      + Number(detalhes.multa || 0)
                      + Number(detalhes.juros || 0);
                    return (
                      <div className="flex justify-between items-baseline">
                        <span className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Total a pagar</span>
                        <span className="text-2xl font-bold" style={{ color: cor, fontFamily: 'Outfit, sans-serif' }}>{fmt(totalSemana)}</span>
                      </div>
                    );
                  })()}
                  {detalhes.valor_debitos_parcela > 0 && (
                    <div className="text-xs mt-2 px-3 py-2 rounded-lg" style={{ backgroundColor: 'rgba(255, 184, 0, 0.08)', border: '1px solid rgba(255, 184, 0, 0.25)', color: '#FFD89B' }}>
                      💡 Você também tem <strong>{fmt(detalhes.valor_debitos_parcela)}</strong> de débito pendente — pague separadamente na aba <strong>Débitos</strong> (PIX diferente).
                    </div>
                  )}
                  {detalhes.mensagem && (
                    <div className="text-xs mt-1" style={{ color: '#64748B' }}>{detalhes.mensagem}</div>
                  )}
                </>
              )}

              {isDebito && (
                <>
                  <div className="border-t border-white/5 my-2"></div>
                  {pag.descricao && (
                    <div className="flex justify-between items-baseline gap-3">
                      <span className="text-sm flex-shrink-0" style={{ color: '#94A3B8' }}>Descrição</span>
                      <span className="text-sm text-white text-right">{pag.descricao}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-baseline">
                    <span className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Valor</span>
                    <span className="text-2xl font-bold" style={{ color: cor, fontFamily: 'Outfit, sans-serif' }}>{fmt(pag.valor)}</span>
                  </div>
                  <div className="text-xs mt-1" style={{ color: '#64748B' }}>
                    Débitos são pagos via PIX FIXO da locadora (não acumulam juros).
                  </div>
                </>
              )}

              {bloqueada && (
                <div
                  className="mt-3 p-3 rounded-xl"
                  style={{ backgroundColor: 'rgba(255, 42, 95, 0.08)', border: '1px solid rgba(255, 42, 95, 0.3)' }}
                >
                  <div className="font-semibold text-sm" style={{ color: '#FF2A5F' }}>🔒 Moto bloqueada</div>
                  <div className="text-xs mt-1" style={{ color: '#FFD0DC' }}>
                    Pague o valor TOTAL acima e envie o comprovante para que o motor seja liberado.
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => { setVerDetalhes(false); onPagar(pag); }}
              className="mt-5 w-full font-semibold py-3.5 px-6 transition-all"
              style={{
                backgroundColor: cor,
                color: '#060A14',
                borderRadius: '999px',
                boxShadow: `0 0 20px ${corGlow}`,
                fontFamily: 'DM Sans, sans-serif',
              }}
              data-testid={`cliente-btn-pagar-modal-${pag.id}`}
            >
              {bloqueada ? 'PAGAR PARA LIBERAR →' : 'PAGAR AGORA →'}
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// ============================================================
// HELPERS WhatsApp Mecânico (Carlão)
// ============================================================
const MECANICO_WHATSAPP = '5513998055527';
const MECANICO_NOME = 'Carlão';

const _saudacaoMecanico = () => {
  const hora = new Date().getHours();
  let periodo = 'Bom dia';
  if (hora >= 12 && hora < 18) periodo = 'Boa tarde';
  else if (hora >= 18 || hora < 5) periodo = 'Boa noite';
  const variacoes = [
    `${periodo}, meu amigo ${MECANICO_NOME}!`,
    `${periodo}, ${MECANICO_NOME} querido!`,
    `Salve ${MECANICO_NOME}, ${periodo.toLowerCase()}!`,
    `Eaí ${MECANICO_NOME}, ${periodo.toLowerCase()}!`,
    `${periodo}, mestre ${MECANICO_NOME}!`,
    `Fala ${MECANICO_NOME}, ${periodo.toLowerCase()}!`,
  ];
  return variacoes[Math.floor(Math.random() * variacoes.length)];
};

const _planoLabel = (plano) => {
  if (!plano) return '—';
  const map = { mensal: 'Mensal', anual: 'Anual', fidelidade: 'Fidelidade' };
  return map[String(plano).toLowerCase()] || String(plano);
};

const abrirWhatsAppMecanico = (me) => {
  const nomeCliente = me?.nome || '—';
  const placa = me?.moto?.placa || me?.locacao?.placa || '—';
  const plano = _planoLabel(me?.locacao?.plano_escolhido);
  const kmAtual = Number(me?.moto?.km_atual || 0).toLocaleString('pt-BR');
  const hoje = new Date().toLocaleDateString('pt-BR');
  const saudacao = _saudacaoMecanico();

  const linhas = [
    saudacao,
    '',
    'Recebi a notificação de manutenção da minha moto e já estou a caminho! 🏍️',
    '',
    '*Dados:*',
    `• Cliente: ${nomeCliente}`,
    `• Placa: ${placa}`,
    `• Plano de locação: ${plano}`,
    `• Data: ${hoje}`,
    `• KM atual: ${kmAtual}`,
    '',
    'Abraço!',
  ];
  const msg = encodeURIComponent(linhas.join('\n'));
  const url = `https://wa.me/${MECANICO_WHATSAPP}?text=${msg}`;
  try {
    window.open(url, '_blank');
  } catch {
    window.location.href = url;
  }
};


// ============================================================
// HOME (próximo pagamento + débitos + atalhos)
// ============================================================
const HomeScreen = ({ me, onIr }) => {
  const [pags, setPags] = useState([]);
  const [debs, setDebs] = useState([]);
  const [manut, setManut] = useState(null);
  const [pixIguais, setPixIguais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [mostrarPushBanner, setMostrarPushBanner] = useState(false);
  const [pagAtivo, setPagAtivo] = useState(0);
  const carrosselRef = useRef(null);
  const pausadoAteRef = useRef(0); // timestamp em ms; auto-rotação só roda se Date.now() > pausadoAteRef.current

  const pausarAutoRotacao = useCallback(() => {
    // Pausa auto-rotação por 10s após qualquer interação do usuário
    pausadoAteRef.current = Date.now() + 10000;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [p, d, m, pix] = await Promise.all([
          api.get('/cliente/pagamentos'),
          api.get('/cliente/debitos'),
          api.get('/cliente/manutencao').catch(() => ({ data: null })),
          api.get('/cliente/pix').catch(() => ({ data: null })),
        ]);
        setPags(p.data || []);
        setDebs(d.data || []);
        setManut(m.data || null);
        setPixIguais(Boolean(pix.data && pix.data.pix_iguais));
      } finally { setCarregando(false); }
    })();
    // Mostra banner pedindo permissão se ainda nunca pediu E o navegador suporta
    if (typeof Notification !== 'undefined' && Notification.permission === 'default'
        && 'serviceWorker' in navigator && 'PushManager' in window
        && !localStorage.getItem('push_banner_dispensado')) {
      setMostrarPushBanner(true);
    }

    // 🆕 Auto-prompt em PWA instalado: detecta modo standalone (app instalado
    // na tela de início) e dispara a permissão NATIVA assim que abre, sem
    // precisar clicar em banner. Só executa na PRIMEIRA abertura em standalone.
    try {
      const isStandalone =
        window.matchMedia?.('(display-mode: standalone)').matches ||
        window.navigator.standalone === true; // iOS Safari
      const jaPediuStandalone = localStorage.getItem('push_autoprompt_standalone_done');
      const navegadorSuporta =
        typeof Notification !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window;

      if (isStandalone && !jaPediuStandalone && navegadorSuporta && Notification.permission === 'default') {
        // Pequeno delay para o DOM estar pronto e dar contexto visual ao usuário
        setTimeout(async () => {
          try {
            const r = await ativarPushNotifications();
            localStorage.setItem('push_autoprompt_standalone_done', '1');
            setMostrarPushBanner(false);
            if (r?.ok) {
              // Notificação imediata confirmando que está ativo
              try {
                new Notification('🔔 Notificações ativadas!', {
                  body: 'Você receberá lembretes de pagamento e manutenção.',
                  icon: '/icons/icon-192.png',
                });
              } catch { /* ignore */ }
            }
          } catch { /* permissão negada → continua normal */ }
        }, 1200);
      }
    } catch { /* ignore */ }
  }, []);

  const ativarPushAgora = async () => {
    const r = await ativarPushNotifications();
    setMostrarPushBanner(false);
    if (r.ok) alert('🔔 Notificações ativadas!');
  };
  const dispensarPush = () => {
    localStorage.setItem('push_banner_dispensado', '1');
    setMostrarPushBanner(false);
  };

  // Lista unificada de pendências: pagamentos semanais + débitos (multas, etc).
  // FILTRO: só mostra na Home itens com vencimento em ≤ 2 dias (ou já atrasados),
  // para não poluir com cobranças muito futuras. As demais ficam no Histórico/Débitos.
  const hojeMs = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const dentroJanela2Dias = (dataVencimento) => {
    if (!dataVencimento) return false;
    const venc = new Date(dataVencimento + 'T00:00:00');
    if (Number.isNaN(venc.getTime())) return false;
    const delta = Math.round((venc.getTime() - hojeMs) / 86400000);
    return delta <= 2; // vencidos (delta<0) + hoje (0) + 1d + 2d
  };
  // Débitos pendentes prontos pra UI (com vencimento alinhado e label de parcela)
  const debitosPendentes = debs
    .filter((d) => (d.status === 'pendente' || d.status === 'parcial')
      && dentroJanela2Dias(d.proxima_parcela_vencimento || d.data_vencimento))
    .map((d) => ({
      ...d,
      data_vencimento: d.proxima_parcela_vencimento || d.data_vencimento,
      valor: Number(
        d.modo_cobranca === 'parcelado'
          ? (d.valor_proxima_cobranca ?? d.valor_parcela_fixa ?? d.valor_restante ?? d.valor ?? 0)
          : (d.valor_restante ?? d.valor ?? 0)
      ),
      _descricao_debito: d.descricao || d.tipo_cobranca || d.tipo || 'Multa',
      _parcela_label: d.modo_cobranca === 'parcelado' && d.proxima_parcela_numero
        ? `${d.proxima_parcela_numero}/${d.parcelas_total}`
        : null,
    }));

  // 🆕 Quando há MAIS DE 1 débito vencendo na mesma data → consolida em UM
  // único card com detalhamento. Se for apenas 1, exibe normalmente.
  const debitosAgrupadosPorData = debitosPendentes.reduce((acc, d) => {
    const k = d.data_vencimento || 'sem-data';
    acc[k] = acc[k] || [];
    acc[k].push(d);
    return acc;
  }, {});
  const cardsDebitos = Object.entries(debitosAgrupadosPorData).map(([dataVenc, lista]) => {
    if (lista.length === 1) {
      return { ...lista[0], _tipo: 'debito' };
    }
    // Consolidado: soma valor e gera detalhamento
    const totalValor = lista.reduce((s, x) => s + Number(x.valor || 0), 0);
    const detalhamento = lista.map((x) => ({
      id: x.id,
      descricao: x._descricao_debito,
      parcela: x._parcela_label,
      valor: Number(x.valor || 0),
      cobranca_id: x.id,
      tem_arquivo: !!(x.arquivo_path_r2 || x.arquivo_path),
      tipo: x.tipo,
    }));
    return {
      id: `debitos-consolidado-${dataVenc}`,
      _tipo: 'debito',
      _consolidado: true,
      _itens: detalhamento,
      data_vencimento: dataVenc,
      valor: totalValor,
      valor_restante: totalValor,
      status: lista.some((x) => x.status === 'parcial') ? 'parcial' : 'pendente',
      _descricao_debito: `${lista.length} débitos`,
      _parcela_label: null,
    };
  });

  const pendentes = [
    ...pags
      .filter((p) => (p.status === 'pendente' || p.status === 'atrasado') && dentroJanela2Dias(p.data_vencimento))
      .map((p) => ({ ...p, _tipo: 'semanal' })),
    ...cardsDebitos,
  ].sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''));

  // Auto-rotação do carrossel: avança a cada 6s, pausa 10s após interação do usuário
  useEffect(() => {
    if (pendentes.length <= 1) return;
    const tick = setInterval(() => {
      if (Date.now() < pausadoAteRef.current) return; // pausado
      const el = carrosselRef.current;
      if (!el) return;
      const cardW = (el.firstChild && el.firstChild.offsetWidth) || el.clientWidth;
      const proximoIdx = (pagAtivo + 1) % pendentes.length;
      el.scrollTo({ left: proximoIdx * (cardW + 16), behavior: 'smooth' });
    }, 6000);
    return () => clearInterval(tick);
  }, [pendentes.length, pagAtivo]);

  const debsPendentes = debs.filter((d) => d.status === 'pendente' || d.status === 'parcial');
  const totalDebs = debsPendentes.reduce((s, d) => s + Number(d.valor_restante || 0), 0);

  if (carregando) {
    return <div className="p-4 text-center text-[#94A3B8]">Carregando...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider" style={{ color: '#94A3B8' }}>Olá,</div>
        <div className="text-2xl font-bold text-white mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{me?.nome || 'Cliente'}</div>
      </div>

      {me?.bloqueio?.status === 'bloqueada' && (
        <div
          className="rounded-3xl p-5 relative overflow-hidden"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 42, 95, 0.18) 0%, rgba(15, 5, 15, 0.95) 100%)',
            border: '1px solid rgba(255, 42, 95, 0.55)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), 0 0 28px rgba(255, 42, 95, 0.35)',
          }}
          data-testid="cliente-banner-motor-bloqueado"
        >
          <div className="flex items-start gap-3">
            <div className="text-3xl leading-none">🚫</div>
            <div className="flex-1">
              <div className="text-base font-bold" style={{ color: '#FF2A5F', fontFamily: 'Outfit, sans-serif' }}>
                Motor da moto BLOQUEADO
              </div>
              <div className="text-sm mt-1.5" style={{ color: '#FFD0DC' }}>
                O motor da sua moto foi bloqueado remotamente via rastreador devido ao atraso no pagamento. Ela não dará partida até a regularização.
              </div>
              {me.bloqueio.motivo && (
                <div className="text-xs mt-2" style={{ color: '#94A3B8' }}>
                  Motivo: {me.bloqueio.motivo}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {pendentes.length > 0 ? (
        <div data-testid="cliente-carrossel-pendentes">
          {/* ⚠️ Aviso de DOIS PIX quando há aluguel E débito pendentes */}
          {(() => {
            const temAluguel = pendentes.some((p) => p._tipo !== 'debito');
            const temDebito = pendentes.some((p) => p._tipo === 'debito');
            if (!(temAluguel && temDebito)) return null;
            return (
              <div
                className="mb-4 rounded-2xl p-4"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 184, 0, 0.13) 0%, rgba(0, 195, 255, 0.10) 100%)',
                  border: '1.5px solid rgba(255, 184, 0, 0.45)',
                }}
                data-testid="cliente-aviso-dois-pix"
              >
                <div className="flex items-start gap-3">
                  <div className="text-2xl">💡</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-amber-200 mb-1">
                      Atenção: aluguel e débito têm chaves PIX DIFERENTES
                    </div>
                    <div className="text-xs text-gray-300 leading-relaxed">
                      Faça <strong className="text-[#00C3FF]">2 PIX separados</strong> — um para o
                      aluguel e outro para o débito/multa.
                      <span className="block mt-1 text-[10px] text-gray-400">
                        A chave PIX correta aparece quando você toca no card de cada pendência. Envie 1 comprovante por pagamento.
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Header do carrossel — só aparece quando há mais de 1 */}
          {pendentes.length > 1 && (
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="text-xs uppercase tracking-wider" style={{ color: '#94A3B8' }}>
                Pendências
              </div>
              <div className="text-xs font-semibold" style={{ color: '#00FF66' }} data-testid="cliente-carrossel-contador">
                {pagAtivo + 1} de {pendentes.length}
              </div>
            </div>
          )}

          {/* Carrossel deslizável horizontal */}
          <div
            ref={carrosselRef}
            onScroll={(e) => {
              const el = e.currentTarget;
              const idx = Math.round(el.scrollLeft / (el.firstChild?.offsetWidth || el.clientWidth));
              if (idx !== pagAtivo) setPagAtivo(idx);
            }}
            onTouchStart={() => { pausarAutoRotacao(); }}
            onMouseDown={() => { pausarAutoRotacao(); }}
            className="-mx-6 px-6 pt-3 pb-4 scrollbar-hide"
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              gap: '16px',
              overflowX: 'auto',
              overflowY: 'visible',
              scrollSnapType: 'x mandatory',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'pan-x',
            }}
          >
            {pendentes.map((p) => (
              <div
                key={p.id}
                style={{
                  flex: '0 0 calc(100vw - 48px)',
                  width: 'calc(100vw - 48px)',
                  minWidth: 'calc(100vw - 48px)',
                  maxWidth: 'calc(100vw - 48px)',
                  scrollSnapAlign: 'center',
                }}
              >
                <PagamentoCard pag={p} pixIguais={pixIguais} temAluguelPendente={pendentes.some(x => x._tipo === 'semanal')} temDebitoPendente={pendentes.some(x => x._tipo === 'debito')} onPagar={(pag) => {
                  if (pag._tipo === 'debito') {
                    onIr('pagar', null, pag);
                  } else {
                    onIr('pagar', pag);
                  }
                }} />
              </div>
            ))}
          </div>

          {/* Dots indicadores */}
          {pendentes.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-3" data-testid="cliente-carrossel-dots">
              {pendentes.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    pausarAutoRotacao();
                    const el = carrosselRef.current;
                    if (el) {
                      const cardW = el.firstChild?.offsetWidth || el.clientWidth;
                      el.scrollTo({ left: i * (cardW + 16), behavior: 'smooth' }); // 16 = gap-4
                    }
                  }}
                  className="transition-all"
                  style={{
                    width: i === pagAtivo ? '20px' : '6px',
                    height: '6px',
                    borderRadius: '999px',
                    background: i === pagAtivo ? '#00FF66' : 'rgba(148, 163, 184, 0.3)',
                    boxShadow: i === pagAtivo ? '0 0 8px rgba(0, 255, 102, 0.5)' : 'none',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  aria-label={`Ver pendência ${i + 1}`}
                />
              ))}
            </div>
          )}

          {/* Dica visual quando há múltiplas */}
          {pendentes.length > 1 && (
            <div className="text-center text-[10px] mt-2" style={{ color: '#64748B' }}>
              ← arraste para o lado para ver outras pendências →
            </div>
          )}
        </div>
      ) : (() => {
        // Conta pendências FUTURAS (>2 dias) que estão ocultas do carrossel
        const pagsFuturos = pags.filter((p) =>
          (p.status === 'pendente' || p.status === 'atrasado') && !dentroJanela2Dias(p.data_vencimento)
        ).length;
        const debsFuturos = debs.filter((d) =>
          (d.status === 'pendente' || d.status === 'parcial')
          && !dentroJanela2Dias(d.proxima_parcela_vencimento || d.data_vencimento)
        ).length;
        const totalFuturos = pagsFuturos + debsFuturos;
        return (
          <div
            className="rounded-3xl p-6 text-center"
            style={{
              background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
              border: '1px solid rgba(0, 255, 102, 0.15)',
            }}
            data-testid="cliente-tudo-em-dia"
          >
            <div className="text-2xl mb-2">🎉</div>
            <div className="text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {totalFuturos > 0 ? 'Nada para pagar agora' : 'Tudo em dia!'}
            </div>
            <div className="text-sm mt-1" style={{ color: '#94A3B8' }}>
              {totalFuturos > 0
                ? `${totalFuturos} pendência${totalFuturos > 1 ? 's' : ''} futura${totalFuturos > 1 ? 's' : ''} (mais de 2 dias) — toque em Pagamentos ou Débitos para ver`
                : 'Sem pagamentos pendentes'}
            </div>
          </div>
        );
      })()}

      {debsPendentes.length > 0 && (
        <button
          onClick={() => onIr('debitos')}
          className="w-full rounded-3xl p-5 text-left transition-all"
          style={{
            background: 'linear-gradient(145deg, rgba(255, 184, 0, 0.08) 0%, rgba(255, 184, 0, 0.03) 100%)',
            border: '1px solid rgba(255, 184, 0, 0.3)',
          }}
          data-testid="cliente-card-debitos"
        >
          <div className="flex justify-between items-center">
            <div>
              <div className="text-xs uppercase tracking-wider" style={{ color: '#FFB800' }}>Débitos em aberto</div>
              <div className="text-2xl font-bold mt-1" style={{ color: '#FFB800', fontFamily: 'Outfit, sans-serif' }}>{fmt(totalDebs)}</div>
            </div>
            <div style={{ color: '#FFB800' }} className="text-sm">Ver →</div>
          </div>
        </button>
      )}

      {manut?.tem_moto && (() => {
        const kmAtual = Number(manut.km_atual || 0);
        const kmAte = Number(manut.km_ate_manutencao || 0);
        const proximaKm = Number(manut.proxima_manutencao_km || 0);
        const progresso = Number(manut.progresso_pct || 0);
        const precisa = !!manut.precisa_manutencao;
        // Cor por faixa de progresso:
        //   - 90%+ OU já vencida → vermelho (crítico)
        //   - 60%-89% → amarelo (alerta)
        //   - <60% → verde (ok)
        const critico = precisa || progresso >= 90;
        const alerta = !critico && progresso >= 60;
        const cor = critico ? '#FF2A5F' : (alerta ? '#FFB800' : '#00FF66');
        const corGlow = critico ? 'rgba(255, 42, 95, 0.15)' : (alerta ? 'rgba(255, 184, 0, 0.12)' : 'rgba(0, 255, 102, 0.08)');
        const corBorda = critico ? 'rgba(255, 42, 95, 0.45)' : (alerta ? 'rgba(255, 184, 0, 0.45)' : 'rgba(0, 255, 102, 0.25)');
        const corBgChip = critico ? 'rgba(255, 42, 95, 0.08)' : (alerta ? 'rgba(255, 184, 0, 0.08)' : 'rgba(0, 255, 102, 0.08)');

        let chipLabel;
        if (precisa) chipLabel = 'Manutenção vencida';
        else if (critico) chipLabel = `Atenção: ${progresso}% — manutenção próxima`;
        else if (alerta) chipLabel = `${progresso}% — agendar em breve`;
        else chipLabel = `${progresso}% até a revisão`;

        return (
          <button
            onClick={() => onIr('minha-moto')}
            className="w-full rounded-3xl p-6 relative overflow-hidden text-left transition-all hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
              border: `1px solid ${corBorda}`,
              boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 24px ${corGlow}`,
            }}
            data-testid="cliente-card-manutencao"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="text-xs uppercase tracking-wider" style={{ color: '#94A3B8' }}>Manutenção da moto</div>
              <div
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{ backgroundColor: corBgChip, color: cor, border: `1px solid ${corBorda}` }}
                data-testid="cliente-chip-manutencao"
              >
                {chipLabel}
              </div>
            </div>

            <div className="flex items-end gap-3">
              <div
                className="text-4xl font-bold tracking-tight"
                style={{ color: cor, fontFamily: 'Outfit, sans-serif', textShadow: `0 0 20px ${corGlow}` }}
                data-testid="cliente-km-atual"
              >
                {kmAtual.toLocaleString('pt-BR')}
              </div>
              <div className="text-base pb-2" style={{ color: '#94A3B8' }}>km atuais</div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between text-xs mb-1.5" style={{ color: '#94A3B8' }}>
                <span>Progresso até a revisão</span>
                <span className="font-semibold" style={{ color: cor }} data-testid="cliente-progresso-pct">{progresso}%</span>
              </div>
              <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, Math.max(0, progresso))}%`,
                    background: cor,
                    boxShadow: `0 0 10px ${corGlow}`,
                  }}
                  data-testid="cliente-barra-progresso"
                />
              </div>
            </div>

            <div className="flex justify-between items-center mt-4 text-xs">
              <div>
                <div style={{ color: '#94A3B8' }}>Próxima revisão</div>
                <div className="font-semibold text-white mt-0.5" data-testid="cliente-proxima-revisao-km">
                  {proximaKm.toLocaleString('pt-BR')} km
                </div>
              </div>
              <div className="text-right">
                <div style={{ color: '#94A3B8' }}>{precisa ? 'Excedeu em' : 'Faltam'}</div>
                <div className="font-semibold mt-0.5" style={{ color: cor }} data-testid="cliente-km-faltando">
                  {Math.abs(kmAte).toLocaleString('pt-BR')} km
                </div>
              </div>
            </div>
          </button>
        );
      })()}

      {/* Botão WhatsApp: agendar manutenção com Carlão.
          Quando manutenção ≥ 90% (crítico) → fica pulsando em vermelho para chamar atenção. */}
      {(() => {
        const progresso = Number(manut?.progresso_pct || 0);
        const precisa = !!manut?.precisa_manutencao;
        const critico = precisa || progresso >= 90;
        return (
          <button
            onClick={() => abrirWhatsAppMecanico(me)}
            className={`w-full rounded-2xl p-4 flex items-center gap-3 transition-all hover:scale-[1.01] ${critico ? 'cliente-btn-agendar-pulse' : ''}`}
            style={{
              background: critico
                ? 'linear-gradient(145deg, rgba(255, 42, 95, 0.18) 0%, rgba(255, 42, 95, 0.05) 100%)'
                : 'linear-gradient(145deg, rgba(37, 211, 102, 0.12) 0%, rgba(37, 211, 102, 0.04) 100%)',
              border: critico ? '1.5px solid rgba(255, 42, 95, 0.7)' : '1px solid rgba(37, 211, 102, 0.45)',
              boxShadow: critico ? '0 4px 16px rgba(255, 42, 95, 0.4)' : '0 4px 16px rgba(37, 211, 102, 0.15)',
            }}
            data-testid="cliente-btn-agendar-manutencao"
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center text-xl"
              style={{ background: critico ? '#FF2A5F' : '#25D366', color: '#fff' }}
            >
              {critico ? '🚨' : '📅'}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {critico ? 'Agendar URGENTE!' : 'Agendar Manutenção'}
              </div>
              <div className="text-xs mt-0.5" style={{ color: critico ? '#FFB4C0' : '#94A3B8' }}>
                {critico
                  ? `Manutenção em ${progresso}% — fale com Carlão agora!`
                  : 'Falar com o mecânico Carlão pelo WhatsApp'}
              </div>
            </div>
            <div className={`text-xs font-bold ${critico ? 'text-[#FF2A5F]' : 'text-[#25D366]'}`}>
              WhatsApp ↗
            </div>
          </button>
        );
      })()}

      <div className="grid grid-cols-2 gap-4 pt-2">
        <button onClick={() => onIr('historico')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
            border: '1px solid rgba(0, 255, 102, 0.15)',
          }}
          data-testid="cliente-tile-historico">
          <Receipt className="w-7 h-7 mb-3" style={{ color: '#00FF66' }} />
          <div className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Histórico</div>
          <div className="text-xs mt-1" style={{ color: '#94A3B8' }}>Pagamentos</div>
        </button>
        <button onClick={() => onIr('comprovantes')}
          className="rounded-2xl p-5 text-left transition-all hover:scale-[1.02]"
          style={{
            background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
            border: '1px solid rgba(0, 255, 102, 0.15)',
          }}
          data-testid="cliente-tile-comprovantes">
          <Upload className="w-7 h-7 mb-3" style={{ color: '#00FF66' }} />
          <div className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Meus Envios</div>
          <div className="text-xs mt-1" style={{ color: '#94A3B8' }}>Comprovantes</div>
        </button>
        <button onClick={() => onIr('minha-moto')}
          className="rounded-2xl p-5 text-left col-span-2 relative overflow-hidden transition-all hover:scale-[1.01]"
          style={{
            background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
            border: '1px solid rgba(0, 255, 102, 0.25)',
            boxShadow: '0 0 24px rgba(0, 255, 102, 0.08)',
            minHeight: '120px',
          }}
          data-testid="cliente-tile-minha-moto">
          <div
            className="absolute inset-0 opacity-20 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1619286875682-1aed54a4bade?crop=entropy&cs=srgb&fm=jpg&w=600&q=70)' }}
          />
          <div className="relative">
            <div className="text-xs uppercase tracking-wider mb-2" style={{ color: '#00FF66' }}>SUA MOTO ALUGADA</div>
            <div className="text-white font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Ver detalhes</div>
            <div className="text-xs mt-1" style={{ color: '#94A3B8' }}>KM, manutenção e histórico</div>
          </div>
        </button>
      </div>
    </div>
  );
};

// ============================================================
// PAGAR (PIX + upload comprovante)
// ============================================================
const PagarScreen = ({ pagamento, debito, onVoltar, onSucesso }) => {
  const [pix, setPix] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  // Cliente paga APENAS o aluguel via PIX da locadora — débitos vão em PIX
  // separado pela aba "Débitos". Por isso descontamos `valor_debitos_parcela`
  // do total. Multa e juros por atraso continuam incluídos.
  const valorAluguelBase = Number(pagamento?.valor_aluguel ?? pagamento?.valor ?? 0);
  const valorAtualizadoFull = Number(pagamento?.valor_atualizado ?? pagamento?.valor ?? 0);
  const valorBaseFull = Number(pagamento?.valor || 0);
  const acrescimoJuros = pagamento ? Math.max(0, valorAtualizadoFull - valorBaseFull) : 0;
  const valorDebitoEmbutido = Number(pagamento?.valor_debitos_parcela || 0);
  // Total sem débitos = aluguel + multa + juros
  // Débito: cobra o valor da PRÓXIMA PARCELA (ou o que resta dela), não o total
  const valor = pagamento
    ? Math.max(0, valorAluguelBase + acrescimoJuros)
    : Number(
        debito?._consolidado
          ? (debito?.valor_restante || 0)
          : (debito?.valor_proxima_cobranca ?? debito?.valor_restante ?? 0)
      );
  const valorBasePag = valorAluguelBase;
  const tipo = pagamento ? 'aluguel' : 'debito';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/cliente/pix');
        setPix(tipo === 'aluguel' ? data.aluguel : data.debitos);
      } catch (e) {
        setErro('Erro ao carregar dados PIX');
      }
    })();
  }, [tipo]);

  const copiar = () => {
    if (!pix?.chave_pix) return;
    navigator.clipboard.writeText(pix.chave_pix);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const enviar = async () => {
    if (!arquivo) { setErro('Selecione o comprovante (imagem ou PDF)'); return; }
    setErro(''); setEnviando(true);
    try {
      const fd = new FormData();
      fd.append('arquivo', arquivo);
      fd.append('valor', String(valor));
      fd.append('tipo', tipo);
      if (pagamento) fd.append('pagamento_id', pagamento.id);
      if (debito) {
        // Débito consolidado (vários débitos pagos no mesmo Pix) — manda lista
        // de IDs separados por vírgula em campo dedicado pro admin distribuir.
        if (debito._consolidado && Array.isArray(debito._itens) && debito._itens.length > 0) {
          const ids = debito._itens.map((it) => it.cobranca_id || it.id).filter(Boolean);
          fd.append('cobrancas_extras_ids', ids.join(','));
          const detalhe = debito._itens.map((it) =>
            `${it.descricao}${it.parcela ? ` ${it.parcela}` : ''}: R$ ${Number(it.valor).toFixed(2).replace('.', ',')}`
          ).join(' | ');
          fd.append('observacoes', `Pagamento consolidado de ${debito._itens.length} débitos — ${detalhe}`);
        } else {
          fd.append('cobranca_extra_id', debito.id);
          fd.append('observacoes', '');
        }
      } else {
        fd.append('observacoes', '');
      }
      const { data: resp } = await api.post('/cliente/comprovantes', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000, // 60s — upload + insert no Mongo (sem IA, que roda em background)
      });
      if (resp?.status === 'recusado_auto') {
        // Comprovante foi salvo mas a análise automática recusou (CNPJ errado / valor errado).
        // Mostra erro pro cliente refazer o Pix. O comprovante fica no histórico para auditoria.
        setErro(resp.motivo_recusa_auto || resp.analise?.mensagem || 'Pagamento não confirmado. Verifique o comprovante.');
        return;
      }
      onSucesso();
    } catch (e) {
      // Mensagem detalhada — facilita diagnóstico em produção
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message || '')) {
        setErro('A conexão demorou muito. Verifique seu sinal e tente novamente.');
      } else if (status === 413 || /muito grande/i.test(detail || '')) {
        setErro('Arquivo muito grande (limite: 10 MB). Tente uma foto menor.');
      } else if (status === 400) {
        setErro(detail || 'Comprovante inválido. Envie uma imagem (JPG/PNG) ou PDF.');
      } else if (status === 401 || status === 403) {
        setErro('Sessão expirada. Por favor, faça login novamente.');
      } else if (typeof detail === 'string') {
        setErro(detail);
      } else {
        setErro(`Erro ao enviar comprovante${status ? ` (${status})` : ''}. Tente novamente em alguns instantes.`);
      }
    } finally { setEnviando(false); }
  };

  return (
    <div className="p-4 space-y-4">
      <button onClick={onVoltar} className="flex items-center gap-2 text-[#00FF66] text-sm" data-testid="cliente-btn-voltar">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>

      <h1 className="text-xl font-bold text-white">
        Pagar {tipo === 'aluguel' ? 'aluguel' : 'débito'}
      </h1>

      {/* ⚠️ Banner GRANDE explicando o tipo + a chave PIX que será mostrada */}
      <div
        className="rounded-2xl p-5"
        style={{
          background: tipo === 'aluguel'
            ? 'linear-gradient(135deg, rgba(0, 195, 255, 0.15) 0%, rgba(0, 195, 255, 0.04) 100%)'
            : 'linear-gradient(135deg, rgba(255, 184, 0, 0.18) 0%, rgba(255, 184, 0, 0.04) 100%)',
          border: `2px solid ${tipo === 'aluguel' ? 'rgba(0, 195, 255, 0.55)' : 'rgba(255, 184, 0, 0.65)'}`,
          boxShadow: tipo === 'aluguel' ? '0 6px 24px rgba(0, 195, 255, 0.15)' : '0 6px 24px rgba(255, 184, 0, 0.2)',
        }}
        data-testid="cliente-pagar-tipo-badge"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="text-3xl">{tipo === 'aluguel' ? '🏍️' : '⚠️'}</div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: tipo === 'aluguel' ? '#00C3FF' : '#FFB800' }}>
              {tipo === 'aluguel' ? '🅰️ ALUGUEL SEMANAL' : '🅱️ DÉBITO / MULTA'}
            </div>
            <div className="text-white font-semibold text-base mt-0.5">
              {tipo === 'aluguel'
                ? 'PIX da sua locadora'
                : 'PIX FIXO da Desloca'}
            </div>
          </div>
        </div>
        <div className="text-[11px] text-gray-200 leading-relaxed border-t border-white/10 pt-3">
          {tipo === 'aluguel' ? (
            <>
              ⚠️ <strong className="text-[#00C3FF]">Atenção:</strong> esta chave é DIFERENTE da chave usada para pagar débitos/multas.
              Use somente para pagar o ALUGUEL semanal.
            </>
          ) : (
            <>
              ⚠️ <strong className="text-[#FFB800]">Atenção:</strong> esta chave é DIFERENTE da chave usada para pagar o aluguel.
              Use somente para pagar DÉBITOS e MULTAS.
            </>
          )}
        </div>
      </div>

      {/* Instruções numeradas claras */}
      <div className="cliente-card border-0 rounded-2xl p-4">
        <div className="text-xs text-[#94A3B8] font-semibold mb-3">📝 Como pagar:</div>
        <ol className="space-y-2 text-sm text-gray-200">
          <li className="flex gap-2">
            <span className="text-[#00FF66] font-bold">1.</span>
            <span>Copie a chave PIX abaixo (toque no botão &ldquo;Copiar&rdquo;)</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#00FF66] font-bold">2.</span>
            <span>Abra o app do seu banco e cole a chave</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#00FF66] font-bold">3.</span>
            <span>Confirme o valor de <strong className="text-[#00FF66]">{fmt(valor)}</strong></span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#00FF66] font-bold">4.</span>
            <span>Pague e <strong>salve o comprovante</strong> (print ou PDF)</span>
          </li>
          <li className="flex gap-2">
            <span className="text-[#00FF66] font-bold">5.</span>
            <span>Envie o comprovante abaixo para confirmação</span>
          </li>
        </ol>
      </div>

      <div className="bg-emerald-600/15 border border-emerald-500/40 rounded-2xl p-5">
        <div className="text-xs text-[#00FF66]/80">Valor a pagar</div>
        <div className="text-3xl font-bold text-[#00FF66] mt-1">{fmt(valor)}</div>
        {pagamento && acrescimoJuros > 0 && (
          <div className="text-xs text-amber-300 mt-2 space-y-0.5" data-testid="cliente-acrescimo-juros">
            <div>• Aluguel + débito: {fmt(valorBasePag)}</div>
            <div>• Multa + juros por atraso: <span className="font-semibold">+ {fmt(acrescimoJuros)}</span></div>
          </div>
        )}
        {pagamento && <div className="text-xs text-[#00FF66]/70 mt-2">Vence em {fmtData(pagamento.data_vencimento)}</div>}
      </div>

      <div className="cliente-card border-0 rounded-2xl p-5">
        <div className="text-xs text-[#94A3B8] font-medium mb-2">Chave Pix (Copia e Cola)</div>
        <div className="bg-slate-800 rounded-lg p-3 font-mono text-sm text-white break-all" data-testid="cliente-pix-chave">
          {pix?.chave_pix || '...'}
        </div>
        <button onClick={copiar} disabled={!pix?.chave_pix}
          className="w-full mt-3 cliente-btn-pill py-3 flex items-center justify-center gap-2"
          data-testid="cliente-btn-copiar-pix">
          {copiado ? <><Check className="w-4 h-4" /> Copiado!</> : <><Copy className="w-4 h-4" /> Copiar chave Pix</>}
        </button>
        {pix?.nome_beneficiario && (
          <div className="text-xs text-[#94A3B8] mt-3 space-y-0.5">
            <div>Beneficiário: <span className="text-gray-200">{pix.nome_beneficiario}</span></div>
            <div>Banco: <span className="text-gray-200">{pix.nome_banco}</span></div>
          </div>
        )}
      </div>

      <div className="cliente-card border-0 rounded-2xl p-5">
        <div className="text-xs text-[#94A3B8] font-medium mb-2">Enviar comprovante</div>
        <p className="text-sm text-gray-300 mb-3">
          Após pagar, envie o comprovante (imagem ou PDF) para confirmar.
        </p>
        <label className="block">
          <input type="file" accept="image/*,application/pdf,.pdf,.heic,.heif"
            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
            className="hidden" data-testid="cliente-input-comprovante" id="comprovante-file" />
          <div className="bg-slate-800 border border-dashed border-slate-600 rounded-lg p-6 text-center cursor-pointer hover:border-emerald-500 transition">
            <Upload className="w-8 h-8 text-[#00FF66] mx-auto mb-2" />
            <div className="text-white text-sm font-medium">
              {arquivo ? arquivo.name : 'Toque para escolher arquivo'}
            </div>
            <div className="text-xs text-gray-500 mt-1">PNG, JPG ou PDF até 10 MB</div>
          </div>
        </label>
        <button type="button" onClick={() => document.getElementById('comprovante-file')?.click()} className="hidden" />
        {erro && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg p-3 mt-3">{erro}</div>}
        <button onClick={enviar} disabled={!arquivo || enviando}
          className="w-full mt-4 cliente-btn-pill py-3"
          data-testid="cliente-btn-enviar-comprovante">
          {enviando ? 'Enviando...' : 'Enviar comprovante'}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// HISTÓRICO
// ============================================================
const HistoricoScreen = ({ onIr }) => {
  const [pags, setPags] = useState([]);
  const [comprovantes, setComprovantes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState('pago'); // 'pago' | 'pendente' | 'atrasado'
  const [abrindo, setAbrindo] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/cliente/pagamentos').then((r) => r.data || []),
      api.get('/cliente/comprovantes').then((r) => r.data || []).catch(() => []),
    ]).then(([pg, cps]) => {
      setPags(pg);
      setComprovantes(cps);
    }).finally(() => setCarregando(false));
  }, []);

  if (carregando) return <div className="p-4 text-center text-[#94A3B8]">Carregando...</div>;

  // === Classificação por aba ===
  const hoje = (() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); })();
  const parseV = (s) => {
    if (!s) return null;
    const d = new Date(s + 'T00:00:00');
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  };

  const pagos = pags.filter((p) => p.status === 'pago')
    .sort((a, b) => (b.data_pagamento || b.data_vencimento || '').localeCompare(a.data_pagamento || a.data_vencimento || ''));

  // Pendente vigente: o mais próximo do hoje, ainda não atrasado.
  const pendentesTodos = pags.filter((p) => p.status === 'pendente' || p.status === 'atrasado');
  const pendenteVigente = pendentesTodos
    .filter((p) => {
      const v = parseV(p.data_vencimento);
      return v !== null && v >= hoje;
    })
    .sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''))[0];

  const atrasados = pendentesTodos
    .filter((p) => {
      const v = parseV(p.data_vencimento);
      return v !== null && v < hoje;
    })
    .sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''));

  // Map comprovante por pagamento_id (último com status aprovado/pendente)
  const compPorPag = {};
  comprovantes.forEach((c) => {
    if (c.pagamento_id) {
      const prev = compPorPag[c.pagamento_id];
      if (!prev || (c.criado_em || '') > (prev.criado_em || '')) {
        compPorPag[c.pagamento_id] = c;
      }
    }
  });

  const verComprovante = async (pagamento_id) => {
    const c = compPorPag[pagamento_id];
    if (!c) {
      alert('Nenhum comprovante encontrado para este pagamento.');
      return;
    }
    setAbrindo(c.id);
    try {
      const r = await api.get(`/cliente/comprovantes/${c.id}/arquivo`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
    } catch {
      alert('Não foi possível abrir o comprovante.');
    } finally {
      setAbrindo(null);
    }
  };

  const abas = [
    { key: 'pago', label: 'Pagos', count: pagos.length },
    { key: 'pendente', label: 'Pendente', count: pendenteVigente ? 1 : 0 },
    { key: 'atrasado', label: 'Atrasados', count: atrasados.length },
  ];

  const docsExibir = aba === 'pago' ? pagos : aba === 'pendente' ? (pendenteVigente ? [pendenteVigente] : []) : atrasados;

  return (
    <div className="p-4 space-y-3" data-testid="cliente-historico-page">
      <button onClick={() => onIr('home')} className="flex items-center gap-2 text-[#00FF66] text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="text-xl font-bold text-white">Histórico de Pagamentos</h1>

      {/* Abas */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-4 px-4" data-testid="cliente-abas-historico">
        {abas.map((a) => {
          const ativo = aba === a.key;
          return (
            <button
              key={a.key}
              onClick={() => setAba(a.key)}
              className="flex-shrink-0 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all"
              style={{
                background: ativo ? 'rgba(0, 255, 102, 0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${ativo ? 'rgba(0, 255, 102, 0.45)' : 'rgba(148, 163, 184, 0.2)'}`,
                color: ativo ? '#00FF66' : '#94A3B8',
                boxShadow: ativo ? '0 0 10px rgba(0, 255, 102, 0.25)' : 'none',
              }}
              data-testid={`cliente-aba-${a.key}`}
            >
              {a.label} ({a.count})
            </button>
          );
        })}
      </div>

      {docsExibir.length === 0 ? (
        <div className="text-center text-[#94A3B8] py-8" data-testid="cliente-historico-vazio">
          {aba === 'pago' && '🎉 Sem pagamentos confirmados ainda.'}
          {aba === 'pendente' && '✓ Nenhum pagamento pendente nesta semana.'}
          {aba === 'atrasado' && '✓ Sem pagamentos atrasados.'}
        </div>
      ) : docsExibir.map((p) => {
        const isPago = p.status === 'pago';
        const semanaNum = p.semana_referencia ?? p.semana ?? null;
        const temComprovante = !!compPorPag[p.id];
        return (
          <div
            key={p.id}
            className="rounded-2xl p-4"
            style={{
              background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
              border: `1px solid ${isPago ? 'rgba(0, 255, 102, 0.25)' : aba === 'atrasado' ? 'rgba(255, 42, 95, 0.30)' : 'rgba(255, 184, 0, 0.30)'}`,
            }}
            data-testid={`pag-card-${p.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                {semanaNum != null ? (
                  <>
                    {/* 🎯 Semana em destaque (grande, colorida pela marca) */}
                    <div
                      className="font-bold leading-tight"
                      style={{
                        fontFamily: 'Outfit, sans-serif',
                        fontSize: '1.5rem',
                        color: isPago ? '#00FF66' : aba === 'atrasado' ? '#FF2A5F' : '#00C3FF',
                        letterSpacing: '-0.01em',
                      }}
                      data-testid={`pag-semana-${p.id}`}
                    >
                      Semana {semanaNum}
                    </div>
                    {/* Data secundária */}
                    <div className="text-[11px] text-[#94A3B8] mt-1" data-testid={`pag-data-${p.id}`}>
                      {isPago && p.data_pagamento
                        ? <>✓ Pago em <span style={{ color: '#00FF66' }}>{fmtData(p.data_pagamento)}</span></>
                        : <>📅 Vence em {fmtData(p.data_vencimento)}</>}
                    </div>
                  </>
                ) : (
                  // Fallback: pagamento sem semana_referencia (legado) — mostra só data
                  <div className="text-white font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {isPago && p.data_pagamento
                      ? `✓ Pago em ${fmtData(p.data_pagamento)}`
                      : `Vencimento ${fmtData(p.data_vencimento)}`}
                  </div>
                )}
              </div>
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0"
                style={{
                  backgroundColor: isPago ? 'rgba(0, 255, 102, 0.12)' : aba === 'atrasado' ? 'rgba(255, 42, 95, 0.12)' : 'rgba(255, 184, 0, 0.12)',
                  color: isPago ? '#00FF66' : aba === 'atrasado' ? '#FF2A5F' : '#FFB800',
                  border: `1px solid ${isPago ? 'rgba(0, 255, 102, 0.4)' : aba === 'atrasado' ? 'rgba(255, 42, 95, 0.4)' : 'rgba(255, 184, 0, 0.4)'}`,
                }}
              >
                {isPago ? '✓ Pago' : aba === 'atrasado' ? 'Atrasado' : 'Pendente'}
              </span>
            </div>

            {/* Pago → botão Ver comprovante (se houver) */}
            {isPago && temComprovante && (
              <button
                onClick={() => verComprovante(p.id)}
                disabled={abrindo === compPorPag[p.id]?.id}
                className="w-full mt-3 py-2 px-3 rounded-full text-xs font-semibold transition-all flex items-center justify-center gap-2"
                style={{
                  background: 'rgba(0, 255, 102, 0.08)',
                  border: '1px solid rgba(0, 255, 102, 0.3)',
                  color: '#00FF66',
                }}
                data-testid={`pag-btn-ver-comprovante-${p.id}`}
              >
                <Eye className="w-4 h-4" />
                {abrindo === compPorPag[p.id]?.id ? 'Abrindo...' : 'Ver comprovante'}
              </button>
            )}
            {isPago && !temComprovante && (
              <div className="text-[10px] text-[#64748B] mt-2 text-center italic">
                Comprovante não disponível (pagamento manual)
              </div>
            )}

            {/* Pendente/Atrasado → botão Pagar */}
            {!isPago && (
              <button
                onClick={() => onIr('pagar', p)}
                className="w-full mt-3 py-2.5 px-4 rounded-full font-bold text-sm transition-all"
                style={{
                  backgroundColor: aba === 'atrasado' ? '#FF2A5F' : '#FFB800',
                  color: '#060A14',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: `0 0 14px ${aba === 'atrasado' ? 'rgba(255, 42, 95, 0.3)' : 'rgba(255, 184, 0, 0.3)'}`,
                }}
                data-testid={`pag-btn-pagar-${p.id}`}
              >
                PAGAR AGORA →
              </button>
            )}
          </div>
        );
      })}

      <div className="text-[10px] text-gray-600 text-center pt-2">
        Os valores não aparecem no histórico — para consultar valores, abra a tela de pagamento.
      </div>
    </div>
  );
};

// ============================================================
// DÉBITOS
// ============================================================
const DebitosScreen = ({ onIr }) => {
  const [debs, setDebs] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [expandido, setExpandido] = useState({}); // {debito_id: bool}

  useEffect(() => {
    api.get('/cliente/debitos').then(({ data }) => setDebs(data || [])).finally(() => setCarregando(false));
  }, []);

  if (carregando) return <div className="p-4 text-center text-[#94A3B8]">Carregando...</div>;

  const toggleExpandido = (id) => setExpandido((s) => ({ ...s, [id]: !s[id] }));

  return (
    <div className="p-4 space-y-3">
      <button onClick={() => onIr('home')} className="flex items-center gap-2 text-[#00FF66] text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="text-xl font-bold text-white">Meus Débitos</h1>
      <p className="text-xs text-[#94A3B8]">Todos os débitos lançados pela locadora — multas, IPVA, taxas avulsas.</p>

      {debs.length === 0 ? (
        <div className="text-center text-[#94A3B8] py-8">🎉 Sem débitos extras.</div>
      ) : debs.map((d) => {
        const pendente = d.status === 'pendente' || d.status === 'parcial';
        const isParcelado = d.modo_cobranca === 'parcelado';
        const progresso = d.valor && d.valor > 0 ? Math.round((d.valor_pago || 0) / d.valor * 100) : 0;
        const corStatus = !pendente ? '#00FF66' : (d.status === 'parcial' ? '#FFB800' : '#FF6B6B');
        const corBg = !pendente ? 'rgba(0, 255, 102, 0.10)' : (d.status === 'parcial' ? 'rgba(255, 184, 0, 0.10)' : 'rgba(255, 107, 107, 0.10)');
        const corBorda = !pendente ? 'rgba(0, 255, 102, 0.30)' : (d.status === 'parcial' ? 'rgba(255, 184, 0, 0.30)' : 'rgba(255, 107, 107, 0.30)');

        return (
          <div
            key={d.id}
            className="rounded-2xl p-4"
            style={{
              background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
              border: `1px solid ${corBorda}`,
              boxShadow: `0 4px 16px rgba(0,0,0,0.3)`,
            }}
            data-testid={`deb-card-${d.id}`}
          >
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {d.titulo || d.descricao || 'Débito'}
                </div>
                {d.titulo && d.descricao && d.descricao !== d.titulo && (
                  <div className="text-xs text-[#94A3B8] mt-0.5 line-clamp-2">{d.descricao}</div>
                )}
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                    style={{
                      backgroundColor: isParcelado ? 'rgba(168, 85, 247, 0.15)' : 'rgba(0, 195, 255, 0.15)',
                      color: isParcelado ? '#C084FC' : '#00C3FF',
                      border: `1px solid ${isParcelado ? 'rgba(168, 85, 247, 0.4)' : 'rgba(0, 195, 255, 0.4)'}`,
                    }}
                    data-testid={`deb-modo-${d.id}`}
                  >
                    {isParcelado ? `📅 Parcelado ${d.parcelas_pagas || 0}/${d.parcelas_total}` : '💸 À vista'}
                  </span>
                </div>
              </div>
              <span
                className="px-2 py-1 rounded-full text-[10px] font-bold uppercase flex-shrink-0"
                style={{ backgroundColor: corBg, color: corStatus, border: `1px solid ${corBorda}` }}
              >
                {d.status === 'pago' ? '✓ Pago' : d.status === 'parcial' ? 'Parcial' : 'Pendente'}
              </span>
            </div>

            {/* Valores */}
            <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
              <div>
                <div className="text-[10px] uppercase text-[#94A3B8]">Original</div>
                <div className="text-white font-semibold">{fmt(d.valor)}</div>
              </div>
              {pendente ? (
                <div className="text-right">
                  <div className="text-[10px] uppercase text-[#94A3B8]">Restante</div>
                  <div className="font-semibold" style={{ color: corStatus }} data-testid={`deb-restante-${d.id}`}>
                    {fmt(d.valor_restante)}
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <div className="text-[10px] uppercase text-[#94A3B8]">Pago</div>
                  <div className="text-emerald-400 font-semibold">{fmt(d.valor_pago)}</div>
                </div>
              )}
            </div>

            {/* Barra de progresso (parcelado ou parcial) */}
            {(isParcelado || d.status === 'parcial') && (
              <div className="mt-3">
                <div className="flex justify-between text-[10px] mb-1" style={{ color: '#94A3B8' }}>
                  <span>Progresso</span>
                  <span style={{ color: corStatus }}>{progresso}%</span>
                </div>
                <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${Math.min(100, progresso)}%`, background: corStatus }}
                  />
                </div>
              </div>
            )}

            {/* Botão expandir/colapsar parcelas */}
            {isParcelado && d.parcelas?.length > 0 && (
              <button
                onClick={() => toggleExpandido(d.id)}
                className="w-full mt-3 text-xs py-1.5 px-3 rounded-full border border-white/10 text-[#94A3B8] hover:text-white transition-colors"
                data-testid={`deb-toggle-parcelas-${d.id}`}
              >
                {expandido[d.id] ? '▲ Esconder cronograma' : `▼ Ver cronograma das ${d.parcelas_total} parcelas`}
              </button>
            )}

            {/* Lista de parcelas (expandida) */}
            {isParcelado && expandido[d.id] && d.parcelas?.length > 0 && (
              <div className="mt-3 space-y-1.5" data-testid={`deb-parcelas-${d.id}`}>
                {d.parcelas.map((p) => {
                  const isPaga = p.status === 'paga';
                  return (
                    <div
                      key={p.numero}
                      className="flex justify-between items-center px-3 py-2 rounded-lg text-xs"
                      style={{
                        backgroundColor: isPaga ? 'rgba(0, 255, 102, 0.06)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${isPaga ? 'rgba(0, 255, 102, 0.2)' : 'rgba(148, 163, 184, 0.15)'}`,
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold"
                          style={{
                            backgroundColor: isPaga ? 'rgba(0, 255, 102, 0.2)' : 'rgba(168, 85, 247, 0.2)',
                            color: isPaga ? '#00FF66' : '#C084FC',
                          }}
                        >
                          {p.numero}
                        </span>
                        <span style={{ color: isPaga ? '#94A3B8' : '#E2E8F0' }}>
                          {fmtData(p.data_vencimento)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={isPaga ? 'line-through text-[#64748B]' : 'text-white font-semibold'}>
                          {fmt(p.valor)}
                        </span>
                        {isPaga ? (
                          <span className="text-[10px] text-[#00FF66]">✓ paga</span>
                        ) : p.parcial ? (
                          <span className="text-[10px] text-[#FFB800]">parcial · resta {fmt(p.valor)}</span>
                        ) : (
                          <span className="text-[10px] text-[#FFB800]">pendente</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Vencimento (avista) */}
            {!isParcelado && d.data_vencimento && (
              <div className="mt-3 text-xs flex items-center justify-between" style={{ color: '#94A3B8' }}>
                <span>Vencimento</span>
                <span className="text-white">{fmtData(d.data_vencimento)}</span>
              </div>
            )}

            {/* 🆕 Botão "Ver auto de infração (PDF)" — quando é multa de trânsito com anexo */}
            {d.tipo === 'multa_transito' && (d.arquivo_path_r2 || d.arquivo_path) && (
              <button
                onClick={() => abrirArquivoMulta(d.id)}
                className="w-full mt-3 py-2 px-3 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: 'rgba(255, 184, 0, 0.10)',
                  border: '1px solid rgba(255, 184, 0, 0.40)',
                  color: '#FFB800',
                  fontFamily: 'DM Sans, sans-serif',
                }}
                data-testid={`deb-btn-ver-pdf-${d.id}`}
              >
                📄 Ver auto de infração (PDF/imagem)
              </button>
            )}

            {/* Botão Pagar (só quando pendente/parcial) */}
            {pendente && (
              <button
                onClick={() => onIr('pagar', null, d)}
                className="w-full mt-3 font-bold py-2.5 px-4 rounded-full text-sm transition-all"
                style={{
                  backgroundColor: corStatus,
                  color: '#060A14',
                  boxShadow: `0 0 16px ${corBg}`,
                  fontFamily: 'DM Sans, sans-serif',
                }}
                data-testid={`deb-btn-pagar-${d.id}`}
              >
                {isParcelado ? `PAGAR PRÓXIMA PARCELA · ${fmt(d.valor_proxima_cobranca ?? d.valor_parcela_fixa ?? d.valor_restante)}` : `PAGAR ${fmt(d.valor_restante)}`}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ============================================================
// MEUS ENVIOS (comprovantes)
// ============================================================
const ComprovantesScreen = ({ onIr }) => {
  const [docs, setDocs] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [abrindo, setAbrindo] = useState(null);
  const [filtro, setFiltro] = useState('todos'); // todos | pendente | aprovado | recusado

  useEffect(() => {
    api.get('/cliente/comprovantes').then(({ data }) => setDocs(data || [])).finally(() => setCarregando(false));
  }, []);

  const verComprovante = async (id) => {
    setAbrindo(id);
    try {
      const resp = await api.get(`/cliente/comprovantes/${id}/arquivo`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Libera o blob depois de um tempinho — a aba já carregou
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert('Não foi possível abrir o comprovante. Tente novamente.');
    } finally {
      setAbrindo(null);
    }
  };

  if (carregando) return <div className="p-4 text-center text-[#94A3B8]">Carregando...</div>;

  const statusColor = (s) => ({
    aprovado: 'text-[#00FF66] bg-emerald-500/10 border-emerald-500/30',
    pendente: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    rejeitado: 'text-red-400 bg-red-500/10 border-red-500/30',
    recusado_auto: 'text-red-400 bg-red-500/10 border-red-500/30',
  })[s] || 'text-[#94A3B8] bg-gray-500/10 border-gray-500/30';

  const statusLabel = (s) => ({
    aprovado: 'Aprovado',
    pendente: 'Pendente',
    rejeitado: 'Rejeitado',
    recusado_auto: 'Recusado auto',
  })[s] || s;

  return (
    <div className="p-4 space-y-3">
      <button onClick={() => onIr('home')} className="flex items-center gap-2 text-[#00FF66] text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="text-xl font-bold text-white">Meus Envios</h1>
      <p className="text-sm text-[#94A3B8]">Comprovantes enviados para a locadora.</p>

      {/* Filtros por status */}
      {(() => {
        const opcoes = [
          { key: 'todos', label: 'Todos' },
          { key: 'pendente', label: 'Pendentes' },
          { key: 'aprovado', label: 'Aprovados' },
          { key: 'recusado', label: 'Recusados' },
        ];
        const contagem = {
          todos: docs.length,
          pendente: docs.filter((d) => d.status === 'pendente').length,
          aprovado: docs.filter((d) => d.status === 'aprovado').length,
          recusado: docs.filter((d) => d.status === 'rejeitado' || d.status === 'recusado_auto').length,
        };
        return (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-4 px-4" data-testid="cliente-filtros-envios">
            {opcoes.map((o) => {
              const ativo = filtro === o.key;
              return (
                <button
                  key={o.key}
                  onClick={() => setFiltro(o.key)}
                  className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                  style={{
                    background: ativo ? 'rgba(0, 255, 102, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${ativo ? 'rgba(0, 255, 102, 0.45)' : 'rgba(148, 163, 184, 0.2)'}`,
                    color: ativo ? '#00FF66' : '#94A3B8',
                    boxShadow: ativo ? '0 0 8px rgba(0, 255, 102, 0.25)' : 'none',
                  }}
                  data-testid={`cliente-filtro-envio-${o.key}`}
                >
                  {o.label} ({contagem[o.key]})
                </button>
              );
            })}
          </div>
        );
      })()}

      {(() => {
        const docsFiltrados = filtro === 'todos'
          ? docs
          : filtro === 'recusado'
            ? docs.filter((d) => d.status === 'rejeitado' || d.status === 'recusado_auto')
            : docs.filter((d) => d.status === filtro);
        if (docsFiltrados.length === 0) {
          return (
            <div className="text-center text-[#94A3B8] py-8" data-testid="cliente-envios-vazio">
              {docs.length === 0 ? 'Você ainda não enviou comprovantes.' : 'Nenhum comprovante neste filtro.'}
            </div>
          );
        }
        return docsFiltrados.map((d) => (
          <div key={d.id} className="cliente-card border-0 rounded-xl p-4" data-testid={`cliente-card-envio-${d.id}`}>
            <div className="flex justify-between items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-lg" data-testid={`cliente-envio-valor-${d.id}`}>{fmt(d.valor)}</div>
                <div className="text-xs text-[#94A3B8] mt-0.5">Tipo: {d.tipo === 'aluguel' ? 'Aluguel' : 'Débito'}</div>
                <div className="text-xs text-[#94A3B8] mt-0.5">Enviado: {fmtData(d.criado_em)}</div>
                {d.observacoes && (
                  <div className="text-xs text-gray-300 mt-1 italic">&ldquo;{d.observacoes}&rdquo;</div>
                )}
                {d.motivo_rejeicao && (
                  <div className="text-xs text-red-300 mt-1">Motivo: {d.motivo_rejeicao}</div>
                )}
                {d.motivo_recusa_auto && (
                  <div className="text-xs text-red-300 mt-1" data-testid={`cliente-envio-motivo-auto-${d.id}`}>
                    Motivo: {d.motivo_recusa_auto}
                  </div>
                )}
              </div>
              <span className={`px-2 py-1 rounded text-xs border whitespace-nowrap ${statusColor(d.status)}`} data-testid={`cliente-envio-status-${d.id}`}>
                {statusLabel(d.status)}
              </span>
            </div>
            <button
              onClick={() => verComprovante(d.id)}
              disabled={abrindo === d.id}
              className="w-full mt-3 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-[#00FF66] border border-emerald-500/30 rounded-lg py-2 text-sm font-medium flex items-center justify-center gap-2"
              data-testid={`cliente-btn-ver-comprovante-${d.id}`}
            >
              <Eye className="w-4 h-4" />
              {abrindo === d.id ? 'Abrindo...' : 'Ver comprovante'}
            </button>
          </div>
        ));
      })()}
    </div>
  );
};

// ============================================================
// MINHA MOTO (acompanhamento de manutenção sincronizado com admin)
// ============================================================
const HistoricoManutencoesScreen = ({ onIr }) => {
  const [manutencoes, setManutencoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [filtro, setFiltro] = useState('todas'); // todas | concluidas | em_andamento

  useEffect(() => {
    api.get('/cliente/manutencoes')
      .then(({ data }) => setManutencoes(data || []))
      .finally(() => setCarregando(false));
  }, []);

  const fmtKm = (n) => n ? `${Number(n).toLocaleString('pt-BR')} km` : null;

  const filtradas = (() => {
    if (filtro === 'concluidas') return manutencoes.filter((m) => m.status === 'concluida' || m.status === 'concluido' || m.data_realizada);
    if (filtro === 'em_andamento') return manutencoes.filter((m) => m.status !== 'concluida' && m.status !== 'concluido' && !m.data_realizada);
    return manutencoes;
  })();

  return (
    <div className="p-4 space-y-3" data-testid="cliente-historico-manutencoes-page">
      <button onClick={() => onIr('minha-moto')} className="flex items-center gap-2 text-[#00FF66] text-sm">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="text-xl font-bold text-white">Todas as manutenções</h1>
      <p className="text-xs text-[#94A3B8]">Histórico completo de serviços realizados na sua moto.</p>

      {/* Filtros */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide -mx-4 px-4">
        {[
          { key: 'todas', label: 'Todas' },
          { key: 'concluidas', label: 'Concluídas' },
          { key: 'em_andamento', label: 'Em andamento' },
        ].map((o) => {
          const ativo = filtro === o.key;
          const c = o.key === 'todas' ? manutencoes.length
            : o.key === 'concluidas' ? manutencoes.filter((m) => m.status === 'concluida' || m.status === 'concluido' || m.data_realizada).length
            : manutencoes.filter((m) => !(m.status === 'concluida' || m.status === 'concluido' || m.data_realizada)).length;
          return (
            <button
              key={o.key}
              onClick={() => setFiltro(o.key)}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
              style={{
                background: ativo ? 'rgba(0, 255, 102, 0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${ativo ? 'rgba(0, 255, 102, 0.45)' : 'rgba(148, 163, 184, 0.2)'}`,
                color: ativo ? '#00FF66' : '#94A3B8',
              }}
              data-testid={`cliente-filtro-manut-${o.key}`}
            >
              {o.label} ({c})
            </button>
          );
        })}
      </div>

      {carregando ? (
        <div className="text-center text-[#94A3B8] py-8">Carregando...</div>
      ) : filtradas.length === 0 ? (
        <div className="text-center text-[#94A3B8] py-8" data-testid="cliente-manut-vazio">
          {manutencoes.length === 0 ? 'Sem manutenções registradas ainda.' : 'Nenhuma manutenção neste filtro.'}
        </div>
      ) : filtradas.map((m) => {
        const isConcluida = m.status === 'concluida' || m.status === 'concluido' || m.data_realizada;
        const itens = (Array.isArray(m.itens_trocados) ? m.itens_trocados : [])
          .concat((Array.isArray(m.pecas_utilizadas) ? m.pecas_utilizadas : [])
            .map((p) => (typeof p === 'string' ? p : (p?.nome || p?.descricao || p?.item))).filter(Boolean));
        return (
          <div
            key={m.id}
            className="rounded-2xl p-4"
            style={{
              background: 'linear-gradient(145deg, #101626 0%, #0B101D 100%)',
              border: `1px solid ${isConcluida ? 'rgba(0, 255, 102, 0.25)' : 'rgba(255, 184, 0, 0.25)'}`,
            }}
            data-testid={`cliente-manut-card-${m.id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {m.descricao || m.tipo_servico || 'Manutenção'}
                </div>
                {m.tipo_servico && m.descricao && m.tipo_servico !== m.descricao && (
                  <div className="text-xs text-[#94A3B8] mt-0.5">{m.tipo_servico}</div>
                )}
              </div>
              <span
                className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0"
                style={{
                  backgroundColor: isConcluida ? 'rgba(0, 255, 102, 0.12)' : 'rgba(255, 184, 0, 0.12)',
                  color: isConcluida ? '#00FF66' : '#FFB800',
                  border: `1px solid ${isConcluida ? 'rgba(0, 255, 102, 0.4)' : 'rgba(255, 184, 0, 0.4)'}`,
                }}
              >
                {isConcluida ? '✓ Concluída' : 'Em andamento'}
              </span>
            </div>

            <div className="flex gap-3 text-xs text-[#94A3B8] mb-2 flex-wrap">
              {(m.data_realizada || m.data_servico) && (
                <span data-testid={`cliente-manut-data-${m.id}`}>📅 {fmtData(m.data_realizada || m.data_servico)}</span>
              )}
              {(m.km_realizada || m.km_servico || m.km_atual) > 0 && (
                <span data-testid={`cliente-manut-km-${m.id}`}>🛣️ {fmtKm(m.km_realizada || m.km_servico || m.km_atual)}</span>
              )}
            </div>

            {itens.length > 0 && (
              <div className="mt-2 pt-2 border-t border-white/5">
                <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color: '#64748B' }}>
                  Serviços / Peças
                </div>
                <div className="space-y-1">
                  {itens.map((it, i) => (
                    <div key={`${i}-${it}`} className="text-xs text-gray-300 flex items-start gap-1.5">
                      <span className="text-[#00FF66] mt-0.5">•</span>
                      <span>{it}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}

      <div className="text-xs text-gray-500 text-center pt-2">
        Os valores das manutenções não são exibidos no app do cliente.
      </div>
    </div>
  );
};


const MinhaMotoScreen = ({ onIr }) => {
  const [data, setData] = useState(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    api.get('/cliente/manutencao')
      .then(({ data }) => setData(data))
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <div className="p-4 text-center text-[#94A3B8]">Carregando...</div>;
  if (!data || !data.tem_moto) {
    return (
      <div className="p-4 space-y-3">
        <button onClick={() => onIr('home')} className="flex items-center gap-2 text-[#00FF66] text-sm">
          <ArrowLeft className="w-4 h-4" /> Voltar
        </button>
        <h1 className="text-xl font-bold text-white">Minha Moto</h1>
        <div className="text-center text-[#94A3B8] py-8">Nenhuma moto ativa encontrada.</div>
      </div>
    );
  }

  const fmtKm = (n) => `${Number(n || 0).toLocaleString('pt-BR')} km`;
  const { moto, km_atual, km_ultima_manutencao, proxima_manutencao_km, km_ate_manutencao,
          precisa_manutencao, progresso_pct, atualizado_em, ultima_manutencao,
          historico, intervalo_padrao_km } = data;

  // Status visual baseado no progresso (60% → atenção, 90% → crítico)
  let statusColor = 'emerald';
  let StatusIcon = CheckCircle2;
  let statusText = 'Em dia';
  let statusBg = 'bg-emerald-500/10 border-emerald-500/30 text-[#00FF66]';
  const _critico = precisa_manutencao || progresso_pct >= 90;
  const _alerta = !_critico && progresso_pct >= 60;
  if (_critico) {
    statusColor = 'red';
    StatusIcon = AlertTriangle;
    statusText = precisa_manutencao ? 'Manutenção necessária' : 'Atenção máxima — próximo da revisão';
    statusBg = 'bg-red-500/10 border-red-500/30 text-red-300';
  } else if (_alerta) {
    statusColor = 'amber';
    StatusIcon = AlertTriangle;
    statusText = 'Agendar manutenção em breve';
    statusBg = 'bg-amber-500/10 border-amber-500/30 text-amber-300';
  }

  const barColor = statusColor === 'emerald' ? 'bg-emerald-500'
                  : statusColor === 'amber' ? 'bg-amber-500' : 'bg-red-500';

  return (
    <div className="p-4 space-y-4" translate="no">
      <button onClick={() => onIr('home')} className="flex items-center gap-2 text-[#00FF66] text-sm" data-testid="cliente-minha-moto-voltar">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="text-xl font-bold text-white">Minha Moto</h1>

      {/* Card identificação */}
      <div
        className="cliente-card border rounded-2xl p-4 flex items-center gap-3"
        style={{ borderColor: _critico ? 'rgba(255,42,95,0.45)' : (_alerta ? 'rgba(255,184,0,0.45)' : 'rgba(0,255,102,0.15)') }}
      >
        <div className="bg-emerald-500/15 p-3 rounded-xl"><Bike className="w-7 h-7 text-[#00FF66]" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold" data-testid="cliente-moto-modelo">{moto.modelo || 'Moto'}</div>
          <div className="text-xs text-[#94A3B8]">Placa: <span className="text-gray-200" data-testid="cliente-moto-placa">{moto.placa}</span>
            {moto.cor && <> · {moto.cor}</>} {moto.ano && <> · {moto.ano}</>}
          </div>
        </div>
      </div>

      {/* Status atual */}
      <div className={`border rounded-2xl p-4 flex items-center gap-3 ${statusBg}`} data-testid="cliente-moto-status">
        <StatusIcon className="w-6 h-6 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-semibold">{statusText}</div>
          <div className="text-xs opacity-80 mt-0.5">
            {precisa_manutencao
              ? `Sua moto ultrapassou em ${fmtKm(Math.abs(km_ate_manutencao))} a marca da próxima revisão.`
              : `Faltam ${fmtKm(km_ate_manutencao)} para a próxima manutenção.`}
          </div>
        </div>
      </div>

      {/* Hodômetro + progresso */}
      <div
        className="cliente-card border rounded-2xl p-4 space-y-3"
        style={{ borderColor: _critico ? 'rgba(255,42,95,0.45)' : (_alerta ? 'rgba(255,184,0,0.45)' : 'rgba(0,255,102,0.15)'), boxShadow: _critico ? '0 0 24px rgba(255,42,95,0.15)' : (_alerta ? '0 0 24px rgba(255,184,0,0.12)' : 'none') }}
      >
        <div className="flex items-center gap-2 text-gray-300 text-sm">
          <Gauge className="w-4 h-4 text-[#00FF66]" />
          KM da moto
        </div>
        <div className="flex items-baseline gap-2">
          <div className="text-3xl font-bold text-white" data-testid="cliente-moto-km-atual">{fmtKm(km_atual)}</div>
          <div className="text-xs text-gray-500">atualizado em {fmtData(atualizado_em)}</div>
        </div>

        {/* Barra de progresso */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-[#94A3B8]">
            <span>Última: {fmtKm(km_ultima_manutencao)}</span>
            <span>Próxima: {fmtKm(proxima_manutencao_km)}</span>
          </div>
          <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
            <div
              className={`h-full ${barColor} transition-all duration-500`}
              style={{ width: `${Math.min(100, progresso_pct)}%` }}
              data-testid="cliente-moto-barra-progresso"
            />
          </div>
          <div className="text-xs text-[#94A3B8] text-center">
            {progresso_pct}% do ciclo de {fmtKm(intervalo_padrao_km)}
          </div>
        </div>
      </div>

      {/* Última manutenção */}
      {ultima_manutencao && (
        <div className="cliente-card border-0 rounded-2xl p-4">
          <div className="flex items-center gap-2 text-gray-300 text-sm mb-2">
            <Wrench className="w-4 h-4 text-[#00FF66]" /> Última manutenção realizada
          </div>
          <div className="text-white font-medium">{ultima_manutencao.descricao || ultima_manutencao.tipo_servico || 'Manutenção'}</div>
          <div className="text-xs text-[#94A3B8] mt-0.5">
            {fmtData(ultima_manutencao.data_realizada)}
            {(ultima_manutencao.km_realizada || ultima_manutencao.km_servico || ultima_manutencao.km_atual) ? ` · ${fmtKm(ultima_manutencao.km_realizada || ultima_manutencao.km_servico || ultima_manutencao.km_atual)}` : ''}
          </div>
          {(() => {
            const itens = (Array.isArray(ultima_manutencao.itens_trocados) ? ultima_manutencao.itens_trocados : [])
              .concat((Array.isArray(ultima_manutencao.pecas_utilizadas) ? ultima_manutencao.pecas_utilizadas : [])
                .map(p => (typeof p === 'string' ? p : (p?.nome || p?.descricao || p?.item))).filter(Boolean));
            if (itens.length === 0) return null;
            return (
              <div className="mt-3 space-y-1" data-testid="cliente-itens-trocados">
                <div className="text-xs uppercase tracking-wider text-[#00FF66] mb-1">Itens trocados</div>
                {itens.map((it, i) => (
                  <div key={`${i}-${it}`} className="text-sm text-white flex gap-2">
                    <span className="text-[#00FF66]">•</span> {it}
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {/* Histórico */}
      {historico && historico.length > 0 && (
        <div className="cliente-card border-0 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-gray-300 text-sm flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[#00FF66]" /> Histórico recente
            </div>
            <button
              onClick={() => onIr('manutencoes')}
              className="text-xs font-semibold px-3 py-1 rounded-full transition-all"
              style={{
                color: '#00FF66',
                border: '1px solid rgba(0, 255, 102, 0.3)',
                background: 'rgba(0, 255, 102, 0.05)',
              }}
              data-testid="cliente-btn-ver-todas-manutencoes"
            >
              Ver todas →
            </button>
          </div>
          <div className="space-y-3">
            {historico.map((h) => {
              const itens = (Array.isArray(h.itens_trocados) ? h.itens_trocados : [])
                .concat((Array.isArray(h.pecas_utilizadas) ? h.pecas_utilizadas : [])
                  .map(p => (typeof p === 'string' ? p : (p?.nome || p?.descricao || p?.item))).filter(Boolean));
              return (
                <div key={h.id} className="py-2 border-b border-slate-800 last:border-0" data-testid={`cliente-historico-manut-${h.id}`}>
                  <div className="text-white text-sm font-medium">{h.descricao || h.tipo_servico || 'Manutenção'}</div>
                  <div className="text-xs text-[#94A3B8] mt-0.5">
                    📅 {fmtData(h.data_realizada)}
                    {(h.km_realizada || h.km_servico || h.km_atual) ? <span data-testid={`cliente-historico-km-${h.id}`}> · 🛣️ {fmtKm(h.km_realizada || h.km_servico || h.km_atual)}</span> : null}
                  </div>
                  {itens.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                      {itens.slice(0, 4).map((it, i) => (
                        <div key={`${i}-${it}`} className="text-xs text-gray-300 flex gap-1.5">
                          <span className="text-[#00FF66]">•</span> {it}
                        </div>
                      ))}
                      {itens.length > 4 && <div className="text-xs text-[#94A3B8]">+ {itens.length - 4} item(ns)</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="text-xs text-gray-500 text-center pt-2">
        Os números são atualizados automaticamente pela locadora.
      </div>
    </div>
  );
};



// ============================================================
// PWA INSTALL — captura prompt do navegador + detecta iOS
// ============================================================
let deferredInstallPrompt = null;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    window.dispatchEvent(new CustomEvent('pwa-install-ready'));
  });
  window.addEventListener('appinstalled', () => {
    deferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });
}

function detectarPlataformaInstall() {
  if (typeof window === 'undefined') return 'desconhecido';
  const ua = navigator.userAgent || '';
  const standalone = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;
  if (standalone) return 'instalado';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

// ============================================================
// PERFIL
// ============================================================
const PerfilScreen = ({ me, onLogout, onTrocarSenha, onIr }) => {
  const [pushStatus, setPushStatus] = useState(() => {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
  });
  const [ativando, setAtivando] = useState(false);
  const [plataforma, setPlataforma] = useState(() => detectarPlataformaInstall());
  const [promptDisponivel, setPromptDisponivel] = useState(!!deferredInstallPrompt);
  const [mostrarTutorialIOS, setMostrarTutorialIOS] = useState(false);

  useEffect(() => {
    const onReady = () => setPromptDisponivel(true);
    const onInstalled = () => { setPromptDisponivel(false); setPlataforma('instalado'); };
    window.addEventListener('pwa-install-ready', onReady);
    window.addEventListener('pwa-installed', onInstalled);
    return () => {
      window.removeEventListener('pwa-install-ready', onReady);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, []);

  const ativarPush = async () => {
    setAtivando(true);
    const r = await ativarPushNotifications();
    setAtivando(false);
    if (r.ok) {
      setPushStatus('granted');
      alert('🔔 Notificações ativadas! Você vai receber avisos importantes direto no celular.');
    } else {
      alert('Não foi possível ativar: ' + r.motivo);
    }
  };

  const recriarPush = async () => {
    setAtivando(true);
    const r = await ativarPushNotifications({ forcarRecriar: true });
    setAtivando(false);
    if (r.ok) {
      setPushStatus('granted');
      alert('🔄 Notificações renovadas com sucesso!');
    } else {
      alert('Não foi possível renovar: ' + r.motivo);
    }
  };

  const instalarApp = async () => {
    if (plataforma === 'ios') {
      setMostrarTutorialIOS(true);
      return;
    }
    if (deferredInstallPrompt) {
      deferredInstallPrompt.prompt();
      const { outcome } = await deferredInstallPrompt.userChoice;
      if (outcome === 'accepted') {
        setPromptDisponivel(false);
        setPlataforma('instalado');
      }
      deferredInstallPrompt = null;
    } else {
      alert('Para instalar, abra esta página no Chrome (Android) ou Safari (iPhone) e use o menu do navegador → "Adicionar à tela de início".');
    }
  };

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-bold text-white">Perfil</h1>
      <div className="cliente-card border-0 rounded-xl p-4 space-y-2">
        <div><div className="text-xs text-[#94A3B8]">Nome</div><div className="text-white">{me?.nome}</div></div>
        <div><div className="text-xs text-[#94A3B8]">CPF</div><div className="text-white">{me?.cpf}</div></div>
        {me?.telefone && <div><div className="text-xs text-[#94A3B8]">Telefone</div><div className="text-white">{me.telefone}</div></div>}
        {me?.moto?.placa && <div><div className="text-xs text-[#94A3B8]">Placa</div><div className="text-white">{me.moto.placa}</div></div>}
      </div>

      {/* Card de notificações */}
      <div className="cliente-card border-0 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Bell className={`w-6 h-6 mt-0.5 ${pushStatus === 'granted' ? 'text-[#00FF66]' : 'text-[#94A3B8]'}`} />
          <div className="flex-1">
            <div className="text-white font-medium">Notificações no celular</div>
            <div className="text-xs text-[#94A3B8] mt-0.5">
              {pushStatus === 'granted' && 'Ativadas — você receberá avisos importantes'}
              {pushStatus === 'default' && 'Receba avisos de pagamento e manutenção direto no celular'}
              {pushStatus === 'denied' && 'Bloqueadas pelo navegador — ative nas configurações'}
              {pushStatus === 'unsupported' && 'Seu navegador não suporta notificações'}
            </div>
          </div>
        </div>
        {pushStatus !== 'granted' && pushStatus !== 'unsupported' && (
          <button onClick={ativarPush} disabled={ativando}
            className="w-full mt-3 cliente-btn-pill py-2 text-sm"
            data-testid="cliente-btn-ativar-push">
            {ativando ? 'Ativando...' : 'Ativar notificações'}
          </button>
        )}
        {pushStatus === 'granted' && (
          <button onClick={recriarPush} disabled={ativando}
            className="w-full mt-3 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-[#00FF66] border border-emerald-500/30 font-medium rounded-lg py-2 text-sm"
            data-testid="cliente-btn-recriar-push">
            {ativando ? 'Renovando...' : '🔄 Renovar inscrição de notificações'}
          </button>
        )}
      </div>

      <button onClick={() => onIr && onIr('atendimento')} className="w-full text-white rounded-xl py-3 text-sm font-semibold flex items-center justify-center gap-2"
        style={{ background: 'linear-gradient(135deg, rgba(0, 255, 102, 0.18) 0%, rgba(0, 255, 102, 0.08) 100%)', border: '1px solid rgba(0, 255, 102, 0.4)' }}
        data-testid="cliente-btn-atendimento">
        💬 Solicitar atendimento
      </button>

      <button onClick={onTrocarSenha} className="w-full bg-slate-800 hover:bg-slate-700 text-white rounded-xl py-3 text-sm font-medium" data-testid="cliente-btn-trocar-senha">
        Trocar senha
      </button>
      <button onClick={onLogout} className="w-full bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 rounded-xl py-3 text-sm font-medium flex items-center justify-center gap-2" data-testid="cliente-btn-sair">
        <LogOut className="w-4 h-4" /> Sair
      </button>

      {/* Card Instalar app */}
      {plataforma !== 'instalado' && (
        <div className="bg-gradient-to-br from-emerald-600/15 to-emerald-700/10 border border-emerald-500/40 rounded-xl p-4 mt-2">
          <div className="flex items-start gap-3">
            <Smartphone className="w-6 h-6 mt-0.5 text-[#00FF66] flex-shrink-0" />
            <div className="flex-1">
              <div className="text-white font-medium">Instalar app na tela inicial</div>
              <div className="text-xs text-gray-300 mt-0.5">
                {plataforma === 'ios' && 'Acesse rápido como um app real, sem precisar abrir o navegador.'}
                {plataforma === 'android' && (promptDisponivel
                  ? 'Toque para instalar o app na sua tela inicial.'
                  : 'Use o menu do Chrome → "Adicionar à tela de início".')}
                {plataforma === 'desktop' && 'Use o ícone de instalação na barra de endereço do navegador.'}
                {plataforma === 'desconhecido' && 'Abra esta página no Chrome (Android) ou Safari (iPhone) para instalar.'}
              </div>
            </div>
          </div>
          <button onClick={instalarApp}
            className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg py-2.5 text-sm flex items-center justify-center gap-2"
            data-testid="cliente-btn-instalar-app">
            <Smartphone className="w-4 h-4" />
            {plataforma === 'ios' ? 'Como instalar no iPhone' : 'Instalar app'}
          </button>
        </div>
      )}
      {plataforma === 'instalado' && (
        <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-xl p-3 flex items-center gap-2 text-[#00FF66] text-sm">
          <CheckCircle2 className="w-5 h-5" /> App instalado na tela inicial
        </div>
      )}

      {/* Tutorial iOS (modal) */}
      {mostrarTutorialIOS && (
        <div className="fixed inset-0 bg-black/70 flex items-end sm:items-center justify-center z-[100] p-4" onClick={() => setMostrarTutorialIOS(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-slate-700 rounded-2xl p-5 max-w-md w-full space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-bold text-lg">Instalar no iPhone</h3>
              <button onClick={() => setMostrarTutorialIOS(false)} className="text-[#94A3B8] hover:text-white" data-testid="cliente-fechar-tutorial-ios">
                <X className="w-5 h-5" />
              </button>
            </div>
            <ol className="space-y-3 text-sm text-gray-200">
              <li className="flex gap-3">
                <span className="bg-emerald-500/20 text-[#00FF66] rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-bold">1</span>
                <span>Abra esta página no <strong className="text-white">Safari</strong> (não funciona no Chrome iOS).</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-emerald-500/20 text-[#00FF66] rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-bold">2</span>
                <span className="flex-1">Toque no botão <Share className="inline w-4 h-4 mx-1 -mt-0.5" /> <strong className="text-white">Compartilhar</strong> (quadrado com seta pra cima, na barra de baixo).</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-emerald-500/20 text-[#00FF66] rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-bold">3</span>
                <span>Role pra baixo e toque em <Plus className="inline w-4 h-4 mx-1 -mt-0.5" /> <strong className="text-white">&ldquo;Adicionar à Tela de Início&rdquo;</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-emerald-500/20 text-[#00FF66] rounded-full w-7 h-7 flex items-center justify-center flex-shrink-0 font-bold">4</span>
                <span>Toque em <strong className="text-white">&ldquo;Adicionar&rdquo;</strong> no canto superior direito. Pronto! 🎉</span>
              </li>
            </ol>
            <button onClick={() => setMostrarTutorialIOS(false)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg py-2.5 text-sm">
              Entendi
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// ROOT
// ============================================================
export default function ClienteApp() {
  const [me, setMe] = useState(null);
  const [tela, setTela] = useState('home'); // home | pagar | historico | debitos | comprovantes | perfil | trocar-senha
  const [contexto, setContexto] = useState({}); // payload entre telas
  const [carregando, setCarregando] = useState(true);
  const [trocarSenhaForced, setTrocarSenhaForced] = useState(false);
  const [comprovanteEnviado, setComprovanteEnviado] = useState(false);

  // Auto-esconde o toast "Comprovante enviado" depois de 4 segundos
  useEffect(() => {
    if (!comprovanteEnviado) return;
    const t = setTimeout(() => setComprovanteEnviado(false), 4000);
    return () => clearTimeout(t);
  }, [comprovanteEnviado]);

  const carregarMe = useCallback(async () => {
    try {
      const { data } = await api.get('/cliente/me');
      setMe(data);
      if (data.senha_provisoria) setTrocarSenhaForced(true);
      setCarregando(false);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // Token realmente inválido/revogado → desloga
        localStorage.removeItem(TOKEN_KEY);
        setMe(null);
        setCarregando(false);
      } else {
        // Erro de REDE (offline, 502, timeout): NÃO desloga — mantém a
        // sessão e tenta reconectar sozinho em alguns segundos.
        setTimeout(() => carregarMe(), 4000);
      }
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem(TOKEN_KEY)) carregarMe();
    else setCarregando(false);
  }, [carregarMe]);

  const onLogin = async (data) => {
    if (data.senha_provisoria) setTrocarSenhaForced(true);
    await carregarMe();
    setTela('home');
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setMe(null);
    setTela('home');
    setTrocarSenhaForced(false);
  };

  const irPara = (novaTela, pagamento = null, debito = null) => {
    setContexto({ pagamento, debito });
    setTela(novaTela);
  };

  if (carregando) {
    return <div className="min-h-screen bg-[#060A14] flex items-center justify-center text-[#94A3B8]" translate="no">Carregando...</div>;
  }
  if (!me) return <LoginScreen onLogin={onLogin} />;
  if (trocarSenhaForced) {
    return <TrocarSenhaScreen onSucesso={() => { setTrocarSenhaForced(false); carregarMe(); }} onLogout={onLogout} />;
  }

  const conteudo = (() => {
    switch (tela) {
      case 'pagar': return <PagarScreen pagamento={contexto.pagamento} debito={contexto.debito} onVoltar={() => setTela('home')} onSucesso={() => { setComprovanteEnviado(true); setTela('home'); }} />;
      case 'historico': return <HistoricoScreen onIr={irPara} />;
      case 'debitos': return <DebitosScreen onIr={irPara} />;
      case 'comprovantes': return <ComprovantesScreen onIr={irPara} />;
      case 'minha-moto': return <MinhaMotoScreen onIr={irPara} />;
      case 'manutencoes': return <HistoricoManutencoesScreen onIr={irPara} />;
      case 'atendimento': return <ClienteAtendimentoScreen api={api} onVoltar={() => setTela('perfil')} />;
      case 'perfil': return <PerfilScreen me={me} onLogout={onLogout} onTrocarSenha={() => setTrocarSenhaForced(true)} onIr={irPara} />;
      default: return <HomeScreen me={me} onIr={irPara} />;
    }
  })();

  return (
    <div className="cliente-app-root min-h-screen text-white cliente-pb-safe" style={{ minHeight: '100dvh', backgroundColor: '#060A14', paddingTop: 'env(safe-area-inset-top)' }} translate="no" data-testid="cliente-app-root">
      {conteudo}

      {/* Bolha flutuante de atendimento — visível em todas as telas exceto na própria tela de atendimento e no fluxo de pagamento (que tem ações críticas) */}
      <AtendimentoFloatingButton
        api={api}
        onAbrir={() => setTela('atendimento')}
        visible={tela !== 'atendimento' && tela !== 'pagar'}
      />

      {/* Toast: comprovante enviado (não bloqueia, auto-some em 4s) */}
      {comprovanteEnviado && (
        <div
          className="fixed top-4 left-4 right-4 z-[60] flex items-start gap-3 p-3 rounded-xl shadow-2xl animate-in fade-in slide-in-from-top duration-300"
          style={{
            background: 'linear-gradient(135deg, rgba(0,255,102,0.95) 0%, rgba(0,217,85,0.95) 100%)',
            color: '#060A14',
            border: '1px solid rgba(0, 255, 102, 0.5)',
            boxShadow: '0 8px 30px rgba(0,255,102,0.35)',
          }}
          data-testid="toast-comprovante-enviado"
          role="alert"
        >
          <div className="text-2xl shrink-0" aria-hidden>✅</div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm">Comprovante enviado!</p>
            <p className="text-xs opacity-80 mt-0.5">Aguarde a confirmação da locadora.</p>
          </div>
          <button
            onClick={() => setComprovanteEnviado(false)}
            className="text-slate-900/70 hover:text-slate-900 text-lg leading-none px-1"
            aria-label="Fechar"
          >
            ×
          </button>
        </div>
      )}

      {/* Bottom Nav (PWA standalone - sem URL bar do Safari) */}
      <nav
        className="cliente-bottom-nav fixed bottom-0 left-0 right-0 z-50 backdrop-blur-xl"
        style={{
          backgroundColor: 'rgba(6, 10, 20, 0.85)',
          borderTop: '1px solid rgba(0, 255, 102, 0.2)',
          boxShadow: '0 -4px 20px rgba(0, 255, 102, 0.05)',
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        <div className="flex justify-around items-center" style={{ height: '64px', flexWrap: 'nowrap' }}>
          {[
            { key: 'home', label: 'Início', Icon: Home },
            { key: 'historico', label: 'Pagamentos', Icon: Receipt },
            { key: 'debitos', label: 'Débitos', Icon: CreditCard },
            { key: 'minha-moto', label: 'Moto', Icon: Bike },
            { key: 'perfil', label: 'Perfil', Icon: User },
          ].map(({ key, label, Icon }) => {
            const ativo = tela === key;
            return (
              <button
                key={key}
                onClick={() => setTela(key)}
                style={{
                  flex: '1 1 0%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  height: '100%',
                  color: ativo ? '#00FF66' : '#94A3B8',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent',
                  filter: ativo ? 'drop-shadow(0 0 6px rgba(0, 255, 102, 0.7))' : 'none',
                  transition: 'all 0.2s ease',
                }}
                data-testid={`cliente-nav-${key}`}
              >
                <Icon size={22} strokeWidth={2} color={ativo ? '#00FF66' : '#94A3B8'} />
                <span style={{ fontSize: '11px', lineHeight: 1, fontWeight: ativo ? 600 : 400 }}>{label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
